import {
  createProductCatalog,
  createQuoteFieldCombinations,
  createQuoteFieldDefinitions,
  createQuoteFieldPool,
  createQuoteFieldPresets,
  REMOVED_QUOTE_FIELD_KEYS,
} from '../mock/productCatalog';
import {
  approvedScenarioForRole,
  approvedScopesForAction,
  createInitialPermissionGovernance,
} from '../permission-center-ui/DisposablePermissionPreview.fixture';
import {
  buildPermissionBusinessFieldRows,
  isDeviceMasterQuoteField,
  quoteDefinitionBindingForKey,
} from './businessFieldDirectory';
import {
  businessModeLabel,
  createBusinessModeCatalog,
  isStableBusinessModeCode,
  REMOVED_SYSTEM_SETTING_KEYS,
} from './businessModes';
import { bindLatestSelectedCalculations } from './calculationLifecycle';
import { isSafePreviewFormulaVersion } from './catalogActions';
import {
  isValidCustomFieldDefinition,
  isValidCustomFieldValue,
  ownerForCustomDefinition,
  validateCustomFieldValuesInState,
} from './customFieldValues';
import {
  DEPARTMENT_COMPANY_MAPPING_SETTING_KEY,
  departmentDirectoryFromNames,
  migrateLegacyDepartmentCompanyMappings,
  validateDepartmentCompanyMappings,
} from './departmentCompanyMapping';
import {
  DEVICE_MASTER_RETIRED_PROPERTIES,
  hydrateDeviceCatalogRecord,
} from './deviceCatalog';
import {
  assertMockStorageCapacity,
  hasValidDeviceMedia,
  projectDeviceMediaForStorage,
} from './deviceMedia';
import {
  assertValidCalculationDefinition,
  calculationAuditFactorKeys,
  hasCalculationCycle,
  replayCalculationFromFactors,
  systemCalculationByRef,
} from './fieldCalculations';
import {
  frozenQuotationHeaderProjectionIssue,
  frozenQuotationListProjectionIssue,
  migrateFrozenQuotationProjectionsFromFacts,
} from './frozenQuotationProjection';
import { createDemoState } from './machine';
import { channelRequiredFieldGrant } from './permissionPolicy';
import { createPricingBaseSnapshot, quoteAmounts } from './quoteTotals';
import {
  createShareFieldCombinations,
  SHARE_PUBLIC_REQUIRED_FIELD_IDS,
  validateShareFieldCombinations,
} from './shareExportFieldCombinations';
import {
  createDefaultShareStyles,
  createShareStyleSnapshot,
  hydrateShareStyles,
  isKnownShareStyleSnapshot,
  isValidShareStyleCatalog,
} from './shareStyles';
import type {
  BusinessModeCode,
  CpqPersistedState,
  CpqState,
  DepartmentCompanyMappingRecord,
  DeviceCatalogRecord,
  DisposablePermissionGovernance,
  ExportTaskRecord,
  PackageCatalogRecord,
  PlanCatalogRecord,
  PublicShareItemSnapshot,
  Quote,
  QuoteColumnSnapshot,
  QuoteFieldDefinitionRecord,
  QuoteItem,
  QuoteItemAdjustmentRecord,
  QuoteSourceSnapshot,
  QuoteSourceType,
  ReceiptRecord,
  RoleCatalogRecord,
  ShareFieldCombinationSnapshot,
  StockReminder,
  StorageLike,
  UserCatalogRecord,
} from './types';
import {
  createSystemFieldRegistry,
  createUnifiedFieldRegistry,
  FIXED_QUOTE_HEADER_FIELD_KEYS,
} from './unifiedFieldRegistry';
import { guardRoleView } from './viewGuard';

export const CPQ_STORAGE_KEY = 'cpq-v2-demo:v5';
export const CPQ_V4_STORAGE_KEY = 'cpq-v2-demo:v4';
export const CPQ_LEGACY_STORAGE_KEY = 'cpq-v2-demo:v3';
export const LEGACY_DEVICE_UPDATED_AT = '2026-08-01T00:00:00.000Z';

/**
 * v4 packages stored an unpriced device list.  Upgrade only that explicit
 * shape to a stable relation. Unknown relation values are rejected rather
 * than guessed from a Device price.
 */
function hydratePackageRelations(
  packages: PackageCatalogRecord[],
  knownDeviceIds: ReadonlySet<string>,
  knownPlanIds: ReadonlySet<string>,
): PackageCatalogRecord[] {
  return packages.map((pkg) => {
    const links = pkg.productLinks;
    if (!Array.isArray(links)) throw new Error(`套餐 ${pkg.id} 的设备关联无效`);
    if (
      !Array.isArray(pkg.planIds) ||
      new Set(pkg.planIds).size !== pkg.planIds.length ||
      pkg.planIds.some((planId) => !knownPlanIds.has(planId))
    ) {
      throw new Error(`套餐 ${pkg.id} 的方案关联不可迁移`);
    }
    const relationIds = new Set<string>();
    const relationDeviceIds = new Set<string>();
    const hydratedLinks = links.map((link, index) => {
      const legacy = link as Partial<typeof link>;
      const relationId =
        legacy.relationId ??
        `${pkg.id}-REL-${String(index + 1).padStart(2, '0')}`;
      const sortOrder = legacy.sortOrder;
      if (
        relationIds.has(relationId) ||
        relationDeviceIds.has(legacy.deviceId ?? '') ||
        (legacy.packageId !== undefined && legacy.packageId !== pkg.id) ||
        typeof legacy.deviceId !== 'string' ||
        !legacy.deviceId.trim() ||
        !knownDeviceIds.has(legacy.deviceId) ||
        !Number.isInteger(sortOrder) ||
        Number(sortOrder) < 0 ||
        (legacy.quantity !== undefined &&
          (!Number.isInteger(legacy.quantity) || legacy.quantity < 1)) ||
        (legacy.packageItemPrice != null &&
          (!Number.isFinite(legacy.packageItemPrice) ||
            legacy.packageItemPrice < 0))
      )
        throw new Error(`套餐 ${pkg.id} 的设备关联不可迁移`);
      relationIds.add(relationId);
      relationDeviceIds.add(legacy.deviceId);
      return {
        relationId,
        packageId: pkg.id,
        deviceId: legacy.deviceId,
        quantity: legacy.quantity ?? 1,
        sortOrder: Number(sortOrder),
        packageItemPrice: legacy.packageItemPrice ?? 0,
      };
    });
    const {
      originalPrice: _retiredOriginalPrice,
      discountedQuote: _retiredDiscountedQuote,
      sign: _retiredSign,
      isCombine: _retiredIsCombine,
      ...stablePackage
    } = pkg as PackageCatalogRecord & {
      originalPrice?: unknown;
      discountedQuote?: unknown;
      sign?: unknown;
      isCombine?: unknown;
    };
    return {
      ...stablePackage,
      productLinks: hydratedLinks,
    };
  });
}

export type CpqLoadStatus =
  | 'missing'
  | 'valid-v5'
  | 'migrated-v4'
  | 'migrated-v3'
  | 'malformed-v5'
  | 'unsupported-v5'
  | 'malformed-v4'
  | 'unsupported-v4'
  | 'malformed-v3'
  | 'unsupported-v3'
  | 'storage-read-error';

export interface CpqLoadResult {
  state: CpqState;
  status: CpqLoadStatus;
  autoPersist: boolean;
  error?: unknown;
}

export type CpqPersistStatus =
  | 'persisted-v5'
  | 'migration-complete'
  | 'migration-deferred'
  | 'skipped'
  | 'storage-write-error'
  | 'legacy-cleanup-error';

export interface CpqPersistResult {
  status: CpqPersistStatus;
  error?: unknown;
}

/** Domain persistence port. Business React code never receives StorageLike. */
export interface CpqRepositoryPort {
  load: () => CpqLoadResult;
  /**
   * Reloads shared business data after an adapter-owned cross-tab signal.
   * A browser asset-backed adapter may asynchronously resolve its own local
   * media references before exposing the refreshed state.
   */
  refresh?: () => CpqLoadResult | Promise<CpqLoadResult>;
  subscribe?: (listener: () => void) => () => void;
  persist: (loaded: CpqLoadResult, state: CpqState) => CpqPersistResult;
  reset: () => CpqState;
  /** Disposable-only cleanup of quote/customer binding facts. */
  clearCustomerBindingHistory?: () => CpqState;
}

/** Replaceable Disposable adapter factory; a future formal API implements the same port. */
export function createDisposableStorageRepository(
  storage: StorageLike,
): CpqRepositoryPort {
  return {
    load: () => loadCpqState(storage),
    persist: (loaded, state) => persistCpqLoadResult(storage, loaded, state),
    reset: () => resetCpqState(storage),
    clearCustomerBindingHistory: () => clearCustomerBindingHistory(storage),
  };
}

type LegacyQuoteItem = Omit<QuoteItem, keyof QuoteSourceSnapshot> & {
  source?: string;
  groupName?: string;
  sourceType?: QuoteItem['sourceType'];
  sourceId?: string;
  sourceName?: string;
};

type LegacyQuote = Omit<Quote, 'items'> & { items: LegacyQuoteItem[] };
interface LegacyStockReminder {
  id: string;
  quoteId: string;
  itemId: string;
  sku: string;
  required: number;
  available: number;
  status: 'sent' | 'resolved';
  syncRevision: number;
  createdAt: string;
}
type LegacyCpqState = Omit<
  CpqState,
  'schemaVersion' | 'quotes' | 'currentActorUserId' | 'currentRoleId'
> & {
  schemaVersion: 3;
  quotes: Record<string, LegacyQuote>;
};

type LegacyV4State = Omit<CpqState, 'schemaVersion' | 'currentRoleId'> & {
  schemaVersion: 4;
  currentRoleId?: string;
};

function legacySnapshot(quote: Quote): QuoteColumnSnapshot[] {
  return quote.customColumns.map((key) => ({
    key,
    label: '未记录/不可用',
    type: 'text',
    width: 'normal',
    note: 'legacy-unavailable：未保存历史字段元数据',
    sourceKind: 'manual',
    placement: 'quote-line',
  }));
}

function isSafeDefinitionVersion(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^definition-v[1-9]\d*$/.test(value) &&
    Number.isSafeInteger(Number(value.slice('definition-v'.length)))
  );
}

function isSafeCalculationVersion(value: unknown): value is string {
  return (
    isSafeDefinitionVersion(value) ||
    (typeof value === 'string' &&
      /^calculation-definition-v[1-9]\d*$/.test(value) &&
      Number.isSafeInteger(
        Number(value.slice('calculation-definition-v'.length)),
      ))
  );
}

function isExplicitLegacySnapshot(field: QuoteColumnSnapshot): boolean {
  return (
    field.note?.startsWith('legacy-unavailable：') === true &&
    field.fieldKey === undefined &&
    field.definitionVersion === undefined &&
    field.formulaVersion === undefined
  );
}

/** One-time localStorage migration only. Runtime permission consumers use
 * canonical field IDs and never consult these retired identities. */
const LEGACY_PERMISSION_FIELD_ID_MIGRATIONS: Record<string, string> = {
  'source:DeviceCatalogRecord.jkyCode': 'definition:jky_code',
  'source:DeviceCatalogRecord.rentalPrice': 'definition:rental_price',
  'field:device.rental_price': 'definition:rental_price',
  'source:DeviceCatalogRecord.unitPrice': 'definition:device_unit_price',
  'source:DeviceCatalogRecord.untaxedPlanPrice':
    'definition:device_untaxed_plan_price',
  'source:DeviceCatalogRecord.retailPriceTax13':
    'definition:retail_price_tax13',
  'source:DeviceCatalogRecord.buyoutFinalPriceTax13':
    'definition:purchase_final_price_tax13',
  'source:DeviceCatalogRecord.dynamicCost': 'definition:dynamic_cost',
  'source:DeviceCatalogRecord.compensationPrice':
    'definition:device_compensation_price',
  'source:DeviceCatalogRecord.name': 'definition:device_name',
  'source:DeviceCatalogRecord.sku': 'definition:device_sku',
  'source:DeviceCatalogRecord.image': 'definition:device_image',
  'source:DeviceCatalogRecord.category': 'definition:device_category',
  'source:DeviceCatalogRecord.stockByWarehouse': 'definition:warehouse_stock',
  'source:DeviceCatalogRecord.replacementIds': 'definition:replacement_device',
  'source:QuoteItem.name': 'definition:device_name',
  'source:QuoteItem.sku': 'definition:device_sku',
  'source:QuoteItem.quantity': 'definition:quantity',
  'source:QuoteItem.discount': 'definition:item_discount',
  'source:QuoteItem.stock': 'definition:warehouse_stock',
  'source:QuoteItem.replacementDeviceId': 'definition:replacement_device',
  'source:quoteTotals.lineTotal': 'definition:product_final_discount_price',
  'source:Quote.title': 'definition:quote_title',
  'source:Quote.customer.name': 'definition:customer_name',
  'source:Quote.customer.phone': 'definition:customer_phone',
  'source:Quote.validUntil': 'definition:valid_until',
  'source:Quote.businessModeCode / businessModeLabelSnapshot':
    'definition:business_mode',
  'source:Quote.deposit': 'definition:deposit',
  'source:Quote.overallDiscount': 'definition:overall_discount',
};

const RETIRED_PERMISSION_FIELD_IDS = new Set([
  ...REMOVED_QUOTE_FIELD_KEYS.map((key) => `definition:${key}`),
  ...DEVICE_MASTER_RETIRED_PROPERTIES.map(
    (property) => `source:DeviceCatalogRecord.${property}`,
  ),
  ...['product_attribute', 'procurement_cycle', 'short_rental_price'].map(
    (key) => `definition:${key}`,
  ),
  'source:PackageCatalogRecord.sign',
  'source:PackageCatalogRecord.isCombine',
]);

function calculationFieldsWithCanonicalOwners(
  definitions: readonly QuoteFieldDefinitionRecord[],
) {
  const canonicalByKey = new Map(
    createUnifiedFieldRegistry(definitions).map((descriptor) => [
      descriptor.definition.key,
      descriptor,
    ]),
  );
  return definitions.map((definition) => {
    const descriptor = canonicalByKey.get(definition.key);
    if (!descriptor) throw new Error(`统一字段计算归属缺失：${definition.key}`);
    return {
      key: definition.key,
      label: definition.label,
      owner: descriptor.owner,
      type: definition.type,
      enabled: definition.enabled,
      calculation: definition.calculation,
      factorApplicability: descriptor.binding.factorApplicability,
    };
  });
}

function assertModernSnapshot(
  field: QuoteColumnSnapshot,
  definitions: Map<string, QuoteFieldDefinitionRecord>,
) {
  if (
    !field.key?.trim() ||
    !field.label?.trim() ||
    ![
      'text',
      'number',
      'money',
      'percent',
      'code',
      'date',
      'single-select',
      'multi-select',
      'boolean',
    ].includes(field.type) ||
    !['normal', 'long'].includes(field.width) ||
    !['system', 'manual'].includes(field.sourceKind) ||
    field.fieldKey !== field.key ||
    !isSafeDefinitionVersion(field.definitionVersion) ||
    (field.placement !== undefined &&
      !['quote-line', 'package-summary', 'quote-header'].includes(
        field.placement,
      ))
  )
    throw new Error('报价字段历史快照版本无效');
  if (field.calculation) {
    const calculationFields = calculationFieldsWithCanonicalOwners([
      ...definitions.values(),
    ]);
    assertValidCalculationDefinition(field.calculation, {
      fields: calculationFields,
      allowSystemCrossOwner: field.sourceKind === 'system',
      currentFieldKey: field.key,
    });
    if (field.calculation.resultType !== field.type)
      throw new Error('报价字段历史快照计算结果类型不匹配');
  }
  if (field.calculationSnapshot) {
    if (
      !isSafeCalculationVersion(field.calculationSnapshot.definitionVersion) ||
      !Number.isFinite(field.calculationSnapshot.result) ||
      Object.values(field.calculationSnapshot.factors).some(
        (value) => !Number.isFinite(value),
      )
    )
      throw new Error('报价字段历史计算事实无效');
  }
  if (
    (field.type === 'single-select' || field.type === 'multi-select') &&
    (!Array.isArray(field.options) ||
      !field.options.length ||
      field.options.some(
        (option) => !option.value.trim() || !option.label.trim(),
      ) ||
      new Set(field.options.map((option) => option.value)).size !==
        field.options.length)
  ) {
    throw new Error('报价字段历史快照选项无效');
  }
  const definition = definitions.get(field.key);
  if (
    definition &&
    (!isSafeDefinitionVersion(definition.governance.definitionVersion) ||
      Number(field.definitionVersion.slice('definition-v'.length)) >
        Number(
          definition.governance.definitionVersion.slice('definition-v'.length),
        ))
  )
    throw new Error('报价字段历史快照定义版本越界');
  if (
    definition?.governance.kind === 'system-calculated' &&
    !(
      isSafePreviewFormulaVersion(field.formulaVersion) ||
      (field.formulaVersion === 'calculation-v1' &&
        quoteDefinitionBindingForKey(field.key)?.calculationRef ===
          definition.governance.formulaRef) ||
      (field.formulaVersion === definition.governance.formulaVersion &&
        quoteDefinitionBindingForKey(field.key)?.calculationRef ===
          definition.governance.formulaRef) ||
      (isSafeCalculationVersion(field.formulaVersion) &&
        quoteDefinitionBindingForKey(field.key)?.calculationRef ===
          definition.governance.formulaRef &&
        systemCalculationByRef(definition.governance.formulaRef ?? '')
          ?.definitionVersion === field.formulaVersion)
    )
  )
    throw new Error('计算字段历史公式版本无效');
  if (
    definition?.calculation &&
    definition.governance.kind === 'custom' &&
    !field.calculation
  )
    throw new Error('计算字段历史快照缺少结构化定义');
}

function quarantineLegacyManualValues(
  item: QuoteItem,
  definitions: Map<string, QuoteFieldDefinitionRecord>,
  legacyInput = false,
): QuoteItem {
  const manualFieldValues: NonNullable<QuoteItem['manualFieldValues']> = {};
  const legacyManualFieldValues: Record<string, unknown> = {
    ...item.legacyManualFieldValues,
  };
  for (const [key, value] of Object.entries(item.manualFieldValues ?? {})) {
    const field = definitions.get(key);
    const owner = field ? ownerForCustomDefinition(field) : null;
    if (!owner) {
      legacyManualFieldValues[key] = value;
      continue;
    }
    if (
      !field ||
      owner !== 'quote-line' ||
      !isValidCustomFieldValue(field, value)
    ) {
      if (legacyInput) {
        legacyManualFieldValues[key] = value;
        continue;
      }
      throw new Error(`报价行企业字段 ${key} 的归属或值无效`);
    }
    manualFieldValues[key] = value;
  }
  return {
    ...item,
    manualFieldValues,
    legacyManualFieldValues,
  };
}

function uniqueByKey<T extends { key: string }>(records: T[]): T[] {
  const seen = new Set<string>();
  return records.filter((record) => {
    if (!record.key || seen.has(record.key)) return false;
    seen.add(record.key);
    return true;
  });
}

function hydratePermissionGovernance(
  raw: unknown,
  roles: RoleCatalogRecord[],
  users: UserCatalogRecord[],
  definitions: QuoteFieldDefinitionRecord[],
): DisposablePermissionGovernance {
  const baseline = createInitialPermissionGovernance(roles, definitions);
  if (raw === undefined) return baseline;
  if (!raw || typeof raw !== 'object') throw new Error('权限治理状态无效');
  const candidate = raw as Partial<DisposablePermissionGovernance>;
  const migrateFieldChannelDefaults = candidate.fieldChannelPolicyVersion !== 2;
  if (
    candidate.kind !== 'disposable-permission-governance-v1' ||
    !Number.isInteger(candidate.permissionVersion) ||
    Number(candidate.permissionVersion) < 1 ||
    !Number.isSafeInteger(candidate.permissionVersion) ||
    !candidate.roles ||
    typeof candidate.roles !== 'object'
  )
    throw new Error('权限治理版本无效');
  if (
    Object.keys(candidate.roles).length !==
      Object.keys(baseline.roles).length ||
    Object.keys(candidate.roles).some((roleId) => !baseline.roles[roleId])
  )
    throw new Error('权限治理包含未知或缺失角色');
  const fieldRows = new Map(
    buildPermissionBusinessFieldRows(definitions).map((row) => [row.id, row]),
  );
  const knownDepartmentIds = new Set(
    users.map(
      (user) => `department:${encodeURIComponent(user.department.trim())}`,
    ),
  );
  const hydratedRoles = Object.fromEntries(
    roles.map((role) => {
      const persisted = candidate.roles?.[role.id];
      if (!persisted || typeof persisted !== 'object')
        throw new Error('权限治理缺失角色');
      if (
        persisted.roleId !== role.id ||
        !Number.isInteger(persisted.publishedVersion) ||
        !Number.isSafeInteger(persisted.publishedVersion) ||
        persisted.publishedVersion < 1 ||
        persisted.publishedVersion > (candidate.permissionVersion as number) ||
        !persisted.featureEnabled ||
        typeof persisted.featureEnabled !== 'object' ||
        !persisted.actions ||
        typeof persisted.actions !== 'object' ||
        !persisted.fieldGrants ||
        typeof persisted.fieldGrants !== 'object' ||
        !persisted.channels ||
        typeof persisted.channels !== 'object' ||
        !persisted.memberAdjustments ||
        typeof persisted.memberAdjustments !== 'object'
      )
        throw new Error('角色权限版本无效');
      const approved = approvedScenarioForRole(role.key);
      const approvedFeatures = new Map(
        approved.features.map((item) => [item.id, item]),
      );
      const approvedActions = new Map(
        approved.features.flatMap((feature) =>
          feature.actions.map((action) => [action.id, action] as const),
        ),
      );
      const approvedChannels = new Map(
        approved.channels.map((channel) => [channel.id, channel]),
      );
      if (
        Object.keys(persisted.featureEnabled).length !==
          approvedFeatures.size ||
        Object.keys(persisted.featureEnabled).some(
          (id) => !approvedFeatures.has(id),
        ) ||
        Object.values(persisted.featureEnabled).some(
          (value) => typeof value !== 'boolean',
        ) ||
        Object.keys(persisted.actions).length !== approvedActions.size ||
        Object.keys(persisted.actions).some((id) => !approvedActions.has(id)) ||
        Object.keys(persisted.channels).length !== approvedChannels.size ||
        Object.keys(persisted.channels).some((id) => !approvedChannels.has(id))
      )
        throw new Error('角色权限草稿结构无效');
      for (const [id, policy] of Object.entries(persisted.actions)) {
        const action = approvedActions.get(id);
        if (
          !action ||
          typeof policy.enabled !== 'boolean' ||
          (policy.enabled && (!action.baseAllowed || action.explicitDeny)) ||
          !approvedScopesForAction(action.scope).includes(policy.scope) ||
          !Array.isArray(policy.specifiedDepartmentIds) ||
          new Set(policy.specifiedDepartmentIds).size !==
            policy.specifiedDepartmentIds.length ||
          (policy.scope === 'specified_departments' &&
            policy.specifiedDepartmentIds.length === 0) ||
          (policy.scope !== 'specified_departments' &&
            policy.specifiedDepartmentIds.length > 0) ||
          policy.specifiedDepartmentIds.some(
            (id) => !knownDepartmentIds.has(id),
          )
        )
          throw new Error('权限动作状态无效');
      }
      const readDependencies: Record<string, string> = {
        'customer.manage': 'customer.read',
        'catalog.manage': 'catalog.read',
        'solution.manage': 'solution.read',
        'quote.edit': 'quote.read',
        'quote.finalize': 'quote.read',
        'quote.share.internal': 'quote.read',
        'quote.share.external': 'quote.read',
        'quote.submit': 'quote.read',
        'quote.export': 'quote.read',
        'inventory.adjust': 'inventory.read',
      };
      for (const [id, policy] of Object.entries(persisted.actions)) {
        const action = approvedActions.get(id);
        const readPermission = action
          ? readDependencies[action.permission]
          : undefined;
        const feature = action
          ? approved.features.find((item) =>
              item.actions.some((candidate) => candidate.id === id),
            )
          : undefined;
        const readAction = readPermission
          ? feature?.actions.find(
              (candidate) => candidate.permission === readPermission,
            )
          : undefined;
        if (
          policy.enabled &&
          readAction &&
          !persisted.actions[readAction.id]?.enabled
        )
          throw new Error('权限动作缺少查看前置');
      }
      const acceptedPersistedFieldIds = new Set([
        ...fieldRows.keys(),
        ...Object.keys(LEGACY_PERMISSION_FIELD_ID_MIGRATIONS),
        ...RETIRED_PERMISSION_FIELD_IDS,
      ]);
      if (
        Object.keys(persisted.fieldGrants).some(
          (id) => !acceptedPersistedFieldIds.has(id),
        )
      ) {
        throw new Error('字段权限状态包含未知项');
      }
      const fieldGrants = Object.fromEntries(
        [...fieldRows.keys()].map((id) => {
          const legacyIds = Object.entries(
            LEGACY_PERMISSION_FIELD_ID_MIGRATIONS,
          )
            .filter(([, canonicalId]) => canonicalId === id)
            .map(([legacyId]) => legacyId);
          const persistedIds = [id, ...legacyIds].filter(
            (candidateId) => persisted.fieldGrants[candidateId] !== undefined,
          );
          const row = fieldRows.get(id);
          const definition = row?.definitionKey
            ? definitions.find((field) => field.key === row.definitionKey)
            : undefined;
          if (persistedIds.length === 0 && !definition?.systemLocked) {
            throw new Error('字段权限状态缺失');
          }
          const grants =
            persistedIds.length > 0
              ? persistedIds.map(
                  (candidateId) => persisted.fieldGrants[candidateId],
                )
              : [baseline.roles[role.id].fieldGrants[id]];
          if (grants.some((grant) => !grant))
            throw new Error('字段权限状态缺失');
          // A duplicate legacy identity is intersected once during hydration so
          // disagreement can only remove access. It never survives the result.
          const migratedDefault = baseline.roles[role.id].fieldGrants[id];
          return [
            id,
            {
              view: grants.every((grant) => grant?.view === true),
              edit: grants.every((grant) => grant?.edit === true),
              export:
                migrateFieldChannelDefaults && migratedDefault.export === false
                  ? false
                  : grants.every((grant) => grant?.export === true),
            },
          ];
        }),
      );
      if (
        Object.keys(fieldGrants).length !== fieldRows.size ||
        Object.keys(fieldGrants).some((id) => !fieldRows.has(id))
      )
        throw new Error('字段权限状态缺失或包含未知项');
      for (const [id, grant] of Object.entries(fieldGrants)) {
        const row = fieldRows.get(id);
        if (
          !row ||
          typeof grant.view !== 'boolean' ||
          typeof grant.edit !== 'boolean' ||
          typeof grant.export !== 'boolean' ||
          (!row.available && (grant.view || grant.edit || grant.export)) ||
          (!row.editable && grant.edit) ||
          ((grant.edit || grant.export) && !grant.view)
        )
          throw new Error('字段权限状态无效');
      }
      const channels = Object.fromEntries(
        Object.entries(persisted.channels).map(([id, policy]) => [
          id,
          {
            ...policy,
            fieldIds: [...fieldRows.keys()].filter((fieldId) => {
              const legacyIds = Object.entries(
                LEGACY_PERMISSION_FIELD_ID_MIGRATIONS,
              )
                .filter(([, canonicalId]) => canonicalId === fieldId)
                .map(([legacyId]) => legacyId);
              const persistedIds = [fieldId, ...legacyIds].filter(
                (candidateId) =>
                  persisted.fieldGrants[candidateId] !== undefined,
              );
              const requiredGrant = channelRequiredFieldGrant(id);
              const grant = fieldGrants[fieldId];
              const baselineChannel = baseline.roles[role.id].channels[id];
              const isLegacyPublicRequiredFieldMigration =
                candidate.fieldChannelPolicyVersion === 2 &&
                persisted.publishedVersion === 1 &&
                id === 'channel:public-link' &&
                (SHARE_PUBLIC_REQUIRED_FIELD_IDS as readonly string[]).includes(
                  fieldId,
                ) &&
                baselineChannel.fieldIds.includes(fieldId);
              const persistedChannelFieldIds = new Set(policy.fieldIds);
              return Boolean(
                persistedIds.length > 0 &&
                  requiredGrant &&
                  grant?.view &&
                  grant[requiredGrant] &&
                  (isLegacyPublicRequiredFieldMigration ||
                    persistedIds.every((candidateId) =>
                      persistedChannelFieldIds.has(candidateId),
                    )) &&
                  (!migrateFieldChannelDefaults ||
                    baselineChannel.fieldIds.includes(fieldId)),
              );
            }),
          },
        ]),
      );
      for (const [id, policy] of Object.entries(channels)) {
        const channel = approvedChannels.get(id);
        const requiredGrant = channelRequiredFieldGrant(id);
        if (
          !channel ||
          !requiredGrant ||
          typeof policy.enabled !== 'boolean' ||
          (policy.enabled && channel.decision !== 'allow') ||
          !Array.isArray(policy.fieldIds) ||
          new Set(policy.fieldIds).size !== policy.fieldIds.length ||
          policy.fieldIds.some((fieldId) => {
            const row = fieldRows.get(fieldId);
            const grant = fieldGrants[fieldId];
            return (
              !row?.available ||
              !grant?.view ||
              !grant[requiredGrant] ||
              (requiredGrant === 'edit' && !row.editable)
            );
          })
        )
          throw new Error('通道字段状态无效');
      }
      for (const [userId, adjustment] of Object.entries(
        persisted.memberAdjustments,
      )) {
        const user = users.find((item) => item.id === userId);
        if (
          !user ||
          user.roleId !== role.id ||
          adjustment.userId !== userId ||
          adjustment.roleId !== role.id ||
          !Array.isArray(adjustment.deniedPermissionIds) ||
          new Set(adjustment.deniedPermissionIds).size !==
            adjustment.deniedPermissionIds.length ||
          adjustment.deniedPermissionIds.some(
            (permission) =>
              ![...approvedActions.values()].some(
                (action) =>
                  action.permission === permission &&
                  action.baseAllowed &&
                  !action.explicitDeny,
              ),
          )
        )
          throw new Error('成员微调状态无效');
      }
      return [
        role.id,
        role.key === 'super_admin'
          ? structuredClone(baseline.roles[role.id])
          : {
              ...structuredClone(persisted),
              fieldGrants,
              channels,
            },
      ];
    }),
  );
  return {
    kind: 'disposable-permission-governance-v1',
    fieldChannelPolicyVersion: 2,
    permissionVersion: candidate.permissionVersion as number,
    roles: hydratedRoles,
  };
}

function isLegacyStockReminder(record: unknown): record is LegacyStockReminder {
  if (!record || typeof record !== 'object') return false;
  const candidate = record as Partial<LegacyStockReminder>;
  return (
    typeof candidate.id === 'string' &&
    candidate.id.trim().length > 0 &&
    typeof candidate.quoteId === 'string' &&
    candidate.quoteId.trim().length > 0 &&
    typeof candidate.itemId === 'string' &&
    candidate.itemId.trim().length > 0 &&
    typeof candidate.sku === 'string' &&
    candidate.sku.trim().length > 0 &&
    Number.isFinite(candidate.required) &&
    Number(candidate.required) >= 0 &&
    Number.isFinite(candidate.available) &&
    Number(candidate.available) >= 0 &&
    (candidate.status === 'sent' || candidate.status === 'resolved') &&
    Number.isInteger(candidate.syncRevision) &&
    Number(candidate.syncRevision) >= 0 &&
    typeof candidate.createdAt === 'string'
  );
}

function hydrateStockReminders(state: CpqState): StockReminder[] {
  return (state.stockReminders as unknown[]).map((rawRecord) => {
    const candidate = rawRecord as Partial<StockReminder>;
    if (candidate.projectionSource !== undefined) {
      return rawRecord as StockReminder;
    }
    if (!isLegacyStockReminder(rawRecord)) {
      return rawRecord as StockReminder;
    }
    const record = rawRecord;
    const bindings = Object.entries(state.quotes).flatMap(
      ([quoteVersionId, quote]) =>
        quote.quoteId === record.quoteId
          ? quote.items
              .filter((item) => item.itemId === record.itemId)
              .map((item) => ({ quoteVersionId, item }))
          : [],
    );
    if (bindings.length !== 1) return record as unknown as StockReminder;
    const [{ quoteVersionId, item }] = bindings;
    return {
      id: record.id,
      quoteId: record.quoteId,
      quoteVersionId,
      quoteItemId: record.itemId,
      skuId: item.sku,
      deviceId: item.deviceId,
      skuSnapshot: record.sku,
      warehouseId: null,
      inventoryRunId: `mock-inventory-r${record.syncRevision}`,
      status: record.status === 'resolved' ? 'RESOLVED' : 'OPEN',
      occurrenceCount: 1,
      requiredQuantity: record.required,
      availableQuantity: record.available,
      rowVersion: 1,
      createdAt: record.createdAt,
      resolvedAt: null,
      projectionSource: 'local-storage-mock',
    } satisfies StockReminder;
  });
}

function hydrateReceiptSources(
  quotes: Record<string, Quote>,
  receipts: ReceiptRecord[],
  shares: CpqState['shares'],
): Record<string, Quote> {
  const claims = new Map<string, ReceiptRecord[]>();
  for (const receipt of receipts) {
    if (
      receipt &&
      typeof receipt === 'object' &&
      typeof receipt.convertedQuoteId === 'string'
    ) {
      claims.set(receipt.convertedQuoteId, [
        ...(claims.get(receipt.convertedQuoteId) ?? []),
        receipt,
      ]);
    }
  }
  return Object.fromEntries(
    Object.entries(quotes).map(([quoteId, quote]) => {
      const candidates = claims.get(quoteId) ?? [];
      const receipt = candidates.length === 1 ? candidates[0] : undefined;
      const matchingShares = receipt
        ? shares.filter((share) => share.id === receipt.shareId)
        : [];
      const share = matchingShares.length === 1 ? matchingShares[0] : undefined;
      const sourceQuotes = receipt
        ? Object.values(quotes).filter(
            (source) =>
              source.quoteId === receipt.sourceQuoteId &&
              source.version === receipt.sourceVersion,
          )
        : [];
      return [
        quoteId,
        quote.sourceReceiptId === undefined &&
        receipt &&
        receipt.status === 'converted' &&
        receipt.convertedQuoteId === quoteId &&
        quote.quoteId === quoteId &&
        share &&
        share.quoteId === receipt.sourceQuoteId &&
        share.quoteVersion === receipt.sourceVersion &&
        sourceQuotes.length === 1 &&
        quote.sourceQuoteId === receipt.sourceQuoteId &&
        quote.sourceVersion === receipt.sourceVersion
          ? { ...quote, sourceReceiptId: receipt.id }
          : quote,
      ];
    }),
  );
}

function hydrateQuoteFieldConfiguration(
  state: CpqState,
  sourceVersion: 3 | 4 | 5 = 5,
): CpqState {
  const seedCatalog = createProductCatalog();
  const departmentDirectory = departmentDirectoryFromNames(
    state.catalog.users.map((user) => user.department),
  );
  const rawDepartmentMappings = (state.catalog as Partial<CpqState['catalog']>)
    .departmentCompanyMappings;
  let departmentCompanyMappings: DepartmentCompanyMappingRecord[];
  if (rawDepartmentMappings !== undefined) {
    if (!Array.isArray(rawDepartmentMappings)) {
      throw new Error('部门-公司映射领域模型不是数组');
    }
    departmentCompanyMappings = structuredClone(rawDepartmentMappings);
    validateDepartmentCompanyMappings(
      departmentCompanyMappings,
      departmentDirectory,
    );
  } else {
    const legacyRows =
      state.catalog.settings[DEPARTMENT_COMPANY_MAPPING_SETTING_KEY];
    if (Array.isArray(legacyRows)) {
      departmentCompanyMappings = migrateLegacyDepartmentCompanyMappings(
        legacyRows,
        departmentDirectory,
      );
    } else if (sourceVersion === 5) {
      throw new Error('现代状态缺少部门-公司映射领域事实');
    } else {
      departmentCompanyMappings = structuredClone(
        seedCatalog.departmentCompanyMappings,
      );
    }
  }
  const protectedSuperRole = seedCatalog.roles.find(
    (role) => role.id === 'ROLE-SUPER-ADMIN' && role.key === 'super_admin',
  );
  const protectedSuperUser = seedCatalog.users.find(
    (user) => user.id === 'U-000' && user.role === 'super_admin',
  );
  if (!protectedSuperRole || !protectedSuperUser)
    throw new Error('超级管理员安全基线缺失');
  if (
    state.catalog.roles.some(
      (role) =>
        (role.key === 'super_admin' || role.id === 'ROLE-SUPER-ADMIN') &&
        (role.key !== 'super_admin' || role.id !== 'ROLE-SUPER-ADMIN'),
    )
  ) {
    throw new Error('超级管理员角色绑定无效');
  }
  const hasProtectedSuperRole = state.catalog.roles.some(
    (role) => role.id === protectedSuperRole.id,
  );
  const needsLegacyRoleBoundaryUpgrade =
    !hasProtectedSuperRole ||
    state.catalog.roles.some(
      (role) =>
        (role.id === 'ROLE-ADMIN' && role.label === 'IT/管理员') ||
        ((role.id === 'ROLE-MANAGER' || role.id === 'ROLE-SALES') &&
          role.label === '客户部/商务部'),
    );
  const legacyRoleUpgradeDefaults = new Map(
    seedCatalog.roles.map((role) => [role.id, role]),
  );
  const legacyRoles = structuredClone(state.catalog.roles).map((role) => {
    if (!needsLegacyRoleBoundaryUpgrade) return role;
    const seed = legacyRoleUpgradeDefaults.get(role.id);
    if (!seed || !['admin', 'manager', 'sales'].includes(role.key)) return role;
    return role.key === 'admin'
      ? structuredClone(seed)
      : { ...role, label: seed.label, description: seed.description };
  });
  const roles = (
    hasProtectedSuperRole
      ? legacyRoles
      : [structuredClone(protectedSuperRole), ...legacyRoles]
  ).map((role) =>
    role.id === protectedSuperRole.id
      ? structuredClone(protectedSuperRole)
      : role,
  );
  const roleIds = new Set(roles.map((role) => role.id));
  if (roleIds.size !== roles.length) {
    throw new Error('角色标识重复');
  }
  const legacyRoleIdForKey = (key: string) => {
    const matches = roles.filter((role) => role.key === key);
    return matches.length === 1 ? matches[0].id : undefined;
  };
  const rawCurrentRoleId = (state as Partial<CpqState>).currentRoleId;
  const currentRoleId =
    sourceVersion === 5
      ? rawCurrentRoleId
      : rawCurrentRoleId || legacyRoleIdForKey(state.role);
  const currentRole = roles.find((role) => role.id === currentRoleId);
  if (
    !currentRoleId ||
    !currentRole ||
    currentRole.status !== 'active' ||
    currentRole.key !== state.role
  ) {
    throw new Error('当前演示角色稳定绑定无效');
  }
  if (
    state.catalog.users.some(
      (user) =>
        (user.role === 'super_admin' || user.id === 'U-000') &&
        (user.role !== 'super_admin' || user.id !== 'U-000'),
    )
  ) {
    throw new Error('超级管理员账号绑定无效');
  }
  const sourceUsers = state.catalog.users.some(
    (user) => user.id === protectedSuperUser.id,
  )
    ? state.catalog.users
    : [protectedSuperUser, ...state.catalog.users];
  const users = sourceUsers.map((user) => {
    const roleId = user.roleId ?? legacyRoleIdForKey(user.role);
    const boundRole = roleId
      ? roles.find((role) => role.id === roleId)
      : undefined;
    if (
      !boundRole ||
      !roleId ||
      !roleIds.has(roleId) ||
      boundRole.key !== user.role
    ) {
      throw new Error(`用户 ${user.username} 的角色绑定无效`);
    }
    return { ...user, roleId };
  });
  const rawActorId = (state as Partial<CpqState>).currentActorUserId;
  const legacyPermissionResourceMissing =
    (state.catalog as Partial<CpqState['catalog']>).permissionGovernance ===
    undefined;
  if (sourceVersion === 5 && legacyPermissionResourceMissing) {
    throw new Error('现代状态缺少权限治理事实');
  }
  const currentActorUserId = rawActorId;
  const currentActor = users.find((user) => user.id === currentActorUserId);
  if (
    (sourceVersion === 5 && currentActorUserId === undefined) ||
    (currentActorUserId !== undefined &&
      (currentActor?.status !== 'active' ||
        currentActor.role !== state.role ||
        currentActor.roleId !== currentRoleId))
  )
    throw new Error('当前演示身份绑定无效');
  const validatedActorUserId = currentActor?.id;
  const seededCustomers = new Map(
    seedCatalog.customers.map((customer) => [customer.customerNo, customer]),
  );
  const customers = state.catalog.customers.map((customer) => {
    if (customer.managerUserId) return customer;
    if (sourceVersion === 5) throw new Error('现代客户缺少资源归属绑定');
    const seeded = seededCustomers.get(customer.customerNo);
    return legacyPermissionResourceMissing && seeded
      ? { ...customer, managerUserId: seeded.managerUserId }
      : customer;
  });
  if (
    customers.some((customer) => {
      const manager = users.find((user) => user.id === customer.managerUserId);
      return !customer.managerUserId || !manager || manager.status !== 'active';
    })
  )
    throw new Error('客户资源归属绑定无效');
  const businessModes =
    state.catalog.businessModes === undefined
      ? createBusinessModeCatalog()
      : structuredClone(state.catalog.businessModes);
  if (
    businessModes.length === 4 &&
    ['rental', 'full', 'buyout', 'repair'].every((code) =>
      businessModes.some((mode) => mode.code === code),
    )
  ) {
    const shortRental = createBusinessModeCatalog().find(
      (mode) => mode.code === 'short_rental',
    );
    if (shortRental) businessModes.push(shortRental);
  }
  const settings = Object.fromEntries(
    Object.entries(state.catalog.settings).filter(
      ([key]) =>
        !REMOVED_SYSTEM_SETTING_KEYS.includes(key as never) &&
        key !== DEPARTMENT_COMPANY_MAPPING_SETTING_KEY,
    ),
  );
  const defaultDefinitions = createQuoteFieldDefinitions();
  const definitionDefaults = new Map(
    defaultDefinitions.map((field) => [field.key, field]),
  );
  if (
    Array.isArray(state.catalog.quoteFieldDefinitions) &&
    uniqueByKey(state.catalog.quoteFieldDefinitions).length !==
      state.catalog.quoteFieldDefinitions.length
  ) {
    throw new Error('字段标识重复');
  }
  if (
    Array.isArray(state.catalog.quoteFieldPresets) &&
    new Set(state.catalog.quoteFieldPresets.map((preset) => preset.id)).size !==
      state.catalog.quoteFieldPresets.length
  ) {
    throw new Error('预设标识重复');
  }
  const removedDefinitionKeys = new Set<string>(REMOVED_QUOTE_FIELD_KEYS);
  const retiredDeviceDefinitionKeys = new Set([
    'product_attribute',
    'procurement_cycle',
    'short_rental_price',
  ]);
  const rawPersistedDefinitions = Array.isArray(
    state.catalog.quoteFieldDefinitions,
  )
    ? state.catalog.quoteFieldDefinitions
    : [];
  const rawPersistedPool = Array.isArray(state.catalog.quoteFieldPool)
    ? state.catalog.quoteFieldPool
    : [];
  if (
    sourceVersion === 5 &&
    rawPersistedDefinitions.some((field) =>
      REMOVED_QUOTE_FIELD_KEYS.includes(field.key as never),
    )
  ) {
    throw new Error('现代字段定义包含已删除脏字段');
  }
  if (
    sourceVersion === 5 &&
    rawPersistedPool.some((entry) =>
      FIXED_QUOTE_HEADER_FIELD_KEYS.includes(entry.fieldKey as never),
    )
  ) {
    throw new Error('固定报价抬头字段不得进入报价字段池');
  }
  if (
    sourceVersion === 5 &&
    rawPersistedPool.some((entry) =>
      REMOVED_QUOTE_FIELD_KEYS.includes(entry.fieldKey as never),
    )
  ) {
    throw new Error('现代字段池包含已删除脏字段');
  }
  const persistedDefinitions = uniqueByKey(rawPersistedDefinitions).filter(
    (field) => !retiredDeviceDefinitionKeys.has(field.key),
  );
  let persistedPool = rawPersistedPool.filter(
    (entry) =>
      !REMOVED_QUOTE_FIELD_KEYS.includes(entry.fieldKey as never) &&
      !FIXED_QUOTE_HEADER_FIELD_KEYS.includes(entry.fieldKey as never) &&
      !retiredDeviceDefinitionKeys.has(entry.fieldKey),
  );
  const customDefinitions = persistedDefinitions
    .filter(
      (field) =>
        !definitionDefaults.has(field.key) &&
        !removedDefinitionKeys.has(field.key),
    )
    .map((field) => {
      const rawGovernance = (field as Partial<typeof field>).governance;
      const legacyCustom = field.sourceKind === 'manual' && !rawGovernance;
      const governance =
        rawGovernance?.fieldKey === field.key
          ? rawGovernance
          : legacyCustom
            ? {
                fieldKey: field.key,
                kind: 'custom' as const,
                classification: 'unclassified' as const,
                required: false,
                definitionVersion: 'definition-v1',
                lockedAttributes: ['fieldKey', 'kind'],
              }
            : undefined;
      if (
        !governance ||
        field.sourceKind !== 'manual' ||
        governance.kind !== 'custom'
      ) {
        throw new Error(`字段 ${field.key} 不是有效的企业自定义字段`);
      }
      if (
        !['ordinary', 'sensitive', 'unclassified'].includes(
          governance.classification,
        )
      ) {
        throw new Error(`字段 ${field.key} 的分类无效`);
      }
      const classification = legacyCustom
        ? 'unclassified'
        : governance.classification;
      const enabled =
        classification === 'unclassified' ? false : field.enabled === true;
      return {
        ...field,
        sourceKind: 'manual' as const,
        systemLocked: false,
        governance: {
          ...governance,
          kind: 'custom' as const,
          classification,
          required: false,
          formulaRef: field.calculation?.calculationRef,
          formulaStatus: undefined,
          formulaVersion: field.calculation?.definitionVersion,
          definitionVersion: governance.definitionVersion ?? 'definition-v1',
        },
        enabled,
        defaultVisible: enabled ? field.defaultVisible === true : false,
      };
    });
  /** System definitions are always rebuilt from the approved registry. Older
   * local labels, duplicate price identities and formula placeholders cannot
   * override the canonical name, type, owner or calculation source. */
  if (customDefinitions.some((field) => !isValidCustomFieldDefinition(field))) {
    throw new Error('企业自定义字段类型、归属或选项无效');
  }
  const registrySystemDefinitions = new Map(
    createSystemFieldRegistry().map(({ definition }) => [
      definition.key,
      definition,
    ]),
  );
  const virtualSystemDefinitions = persistedPool.flatMap((entry) => {
    const definition = registrySystemDefinitions.get(entry.fieldKey);
    return definition && !definitionDefaults.has(entry.fieldKey)
      ? [definition]
      : [];
  });
  const definitions = [
    ...defaultDefinitions,
    ...customDefinitions,
    ...virtualSystemDefinitions,
  ];
  const calculationFields = calculationFieldsWithCanonicalOwners(definitions);
  for (const definition of definitions) {
    if (!definition.calculation) continue;
    assertValidCalculationDefinition(definition.calculation, {
      fields: calculationFields,
      allowSystemCrossOwner: definition.systemLocked,
      currentFieldKey: definition.key,
    });
  }
  if (hasCalculationCycle(calculationFields))
    throw new Error('计算字段存在循环依赖');
  const defaultPresets = createQuoteFieldPresets();
  const defaultPool = createQuoteFieldPool();
  const defaultCombinations = createQuoteFieldCombinations();
  /**
   * v5 local state created before the explicit quote-pool membership contract
   * was introduced contains every system definition (except fixed headers).
   * That shape is distinguishable without a field-name deny-list: it contains
   * every current registry system key outside the canonical default pool.  Drop
   * only that legacy seed residue; user-added custom fields and a later
   * explicit system-field pool entry remain intact.
   */
  const defaultPoolKeys = new Set(defaultPool.map((entry) => entry.fieldKey));
  const systemDefinitionKeys = new Set(
    defaultDefinitions
      .filter(
        (field) => !FIXED_QUOTE_HEADER_FIELD_KEYS.includes(field.key as never),
      )
      .map((field) => field.key),
  );
  const legacyOnlySystemKeys = [...systemDefinitionKeys].filter(
    (fieldKey) => !defaultPoolKeys.has(fieldKey),
  );
  const persistedSystemKeys = new Set(
    persistedPool
      .map((entry) => entry.fieldKey)
      .filter((fieldKey) => systemDefinitionKeys.has(fieldKey)),
  );
  const isLegacyFullSystemPool =
    legacyOnlySystemKeys.length > 0 &&
    legacyOnlySystemKeys.every((fieldKey) => persistedSystemKeys.has(fieldKey));
  if (isLegacyFullSystemPool) {
    persistedPool = persistedPool.filter(
      (entry) =>
        !systemDefinitionKeys.has(entry.fieldKey) ||
        defaultPoolKeys.has(entry.fieldKey),
    );
  }
  const presetDefaults = new Map(
    defaultPresets.map((preset) => [preset.id, preset]),
  );
  const knownKeys = new Set(definitions.map((field) => field.key));
  const presetAllowedKeys = new Set(
    definitions
      .filter(
        (field) =>
          field.enabled && field.governance.classification === 'ordinary',
      )
      .map((field) => field.key),
  );
  const persistedPresets = Array.isArray(state.catalog.quoteFieldPresets)
    ? state.catalog.quoteFieldPresets
    : [];
  const customPresets = persistedPresets
    .filter((preset) => !presetDefaults.has(preset.id))
    .map((preset) => {
      if (preset.systemLocked || preset.isDefault) {
        throw new Error(`预设 ${preset.id} 的归属或治理属性无效`);
      }
      return {
        ...preset,
        fieldKeys: [
          ...new Set(
            preset.fieldKeys.filter(
              (key) =>
                presetAllowedKeys.has(key) && !removedDefinitionKeys.has(key),
            ),
          ),
        ],
        enabled: preset.enabled !== false,
        isDefault: false,
        systemLocked: false,
        fieldOverrides: Object.fromEntries(
          Object.entries(preset.fieldOverrides ?? {}).filter(
            ([key, override]) =>
              presetAllowedKeys.has(key) &&
              !removedDefinitionKeys.has(key) &&
              typeof override.displayLabel === 'string' &&
              override.displayLabel.trim().length > 0 &&
              Number.isInteger(override.sortOrder) &&
              Number.isFinite(override.sortOrder) &&
              override.sortOrder > 0,
          ),
        ),
      };
    });
  /** Locked presets are rebuilt from the approved registry. A stale local
   * preset may neither remove a new mandatory field nor restore a retired key. */
  const presets = [...defaultPresets, ...customPresets];
  if (persistedPool.length > 0) {
    const ids = persistedPool.map((entry) => entry.id);
    const keys = persistedPool.map((entry) => entry.fieldKey);
    if (
      new Set(ids).size !== ids.length ||
      new Set(keys).size !== keys.length
    ) {
      throw new Error('字段池标识或来源字段重复');
    }
    if (
      persistedPool.some((entry) => {
        const source = definitions.find(
          (field) => field.key === entry.fieldKey,
        );
        if (definitionDefaults.has(entry.fieldKey)) return false;
        return !(
          typeof entry.id === 'string' &&
          entry.id.trim().length > 0 &&
          source?.enabled === true &&
          source.governance.classification === 'ordinary' &&
          typeof entry.displayLabel === 'string' &&
          entry.displayLabel.trim().length > 0 &&
          (entry.placement === 'quote-line' ||
            entry.placement === 'package-summary' ||
            entry.placement === 'quote-header') &&
          entry.snapshotPolicy === 'freeze-on-select' &&
          typeof entry.enabled === 'boolean' &&
          Number.isInteger(entry.sortOrder) &&
          entry.sortOrder > 0
        );
      })
    )
      throw new Error('字段池持久化状态无效');
  }
  const customDefinitionKeys = new Set(
    customDefinitions.map((field) => field.key),
  );
  const customPool = persistedPool
    .filter((entry) => customDefinitionKeys.has(entry.fieldKey))
    .map((entry, index) => ({
      ...entry,
      displayLabel: entry.displayLabel.trim(),
      placement: entry.placement,
      snapshotPolicy: 'freeze-on-select' as const,
      enabled: entry.enabled,
      mandatory: false,
      defaultVisible: entry.defaultVisible === true,
      sortOrder: defaultPool.length + index + 1,
    }));
  const referencedSystemPool = persistedPool
    .filter(
      (entry) =>
        systemDefinitionKeys.has(entry.fieldKey) &&
        !defaultPoolKeys.has(entry.fieldKey),
    )
    .map((entry, index) => ({
      ...entry,
      displayLabel:
        entry.displayLabel.trim() ||
        registrySystemDefinitions.get(entry.fieldKey)?.label ||
        entry.fieldKey,
      mandatory: false,
      defaultVisible: entry.defaultVisible === true,
      sortOrder: defaultPool.length + customPool.length + index + 1,
    }));
  /** System pool metadata is canonical and cannot be overridden by stale
   * localStorage. Object-field references retain their registry identity and
   * are added only after a user explicitly places them in the pool. */
  const pool = [...defaultPool, ...customPool, ...referencedSystemPool];
  const poolKeys = new Set(
    pool.filter((entry) => entry.enabled).map((entry) => entry.fieldKey),
  );
  const quoteLinePoolKeys = new Set(
    pool
      .filter((entry) => entry.enabled && entry.placement === 'quote-line')
      .map((entry) => entry.fieldKey),
  );
  const lockedCombinationDefaults = new Map(
    defaultCombinations
      .filter((item) => item.systemLocked)
      .map((item) => [item.id, item]),
  );
  if (Array.isArray(state.catalog.quoteFieldCombinations)) {
    const ids = state.catalog.quoteFieldCombinations.map((item) => item.id);
    if (
      new Set(ids).size !== ids.length ||
      state.catalog.quoteFieldCombinations.some(
        (item) =>
          Array.isArray(item.fieldKeys) &&
          new Set(item.fieldKeys).size !== item.fieldKeys.length,
      )
    )
      throw new Error('快捷组合标识或字段重复');
    if (
      state.catalog.quoteFieldCombinations.filter(
        (item) => item.isDefault === true,
      ).length > 1
    ) {
      throw new Error('只能有一个默认快捷组合');
    }
    if (
      state.catalog.quoteFieldCombinations.some(
        (item) =>
          !(
            typeof item.id === 'string' &&
            item.id.trim().length > 0 &&
            typeof item.name === 'string' &&
            item.name.trim().length > 0 &&
            Array.isArray(item.fieldKeys) &&
            item.fieldKeys.length > 0 &&
            new Set(item.fieldKeys).size === item.fieldKeys.length &&
            item.fieldKeys.every((key) => poolKeys.has(key)) &&
            typeof item.enabled === 'boolean' &&
            (item.scope === undefined ||
              item.scope === '全公司' ||
              item.scope === '本部门' ||
              item.scope === '管理层') &&
            (item.isDefault === undefined ||
              typeof item.isDefault === 'boolean') &&
            typeof item.systemLocked === 'boolean' &&
            Number.isInteger(item.sortOrder) &&
            item.sortOrder > 0
          ),
      )
    )
      throw new Error('快捷组合持久化状态无效');
    if (
      [...lockedCombinationDefaults].some(
        ([id]) =>
          !state.catalog.quoteFieldCombinations.some((item) => item.id === id),
      ) ||
      state.catalog.quoteFieldCombinations.some(
        (item) =>
          (lockedCombinationDefaults.has(item.id) && !item.systemLocked) ||
          (item.systemLocked && !lockedCombinationDefaults.has(item.id)),
      )
    ) {
      throw new Error('系统快捷组合治理属性无效');
    }
  }
  const combinations = Array.isArray(state.catalog.quoteFieldCombinations)
    ? state.catalog.quoteFieldCombinations.map((item) => ({
        ...item,
        scope: item.scope ?? '全公司',
        isDefault: item.isDefault ?? false,
      }))
    : defaultCombinations.filter((item) =>
        item.fieldKeys.every((key) => poolKeys.has(key)),
      );
  if (
    Array.isArray(state.catalog.quoteFieldCombinations) &&
    [...lockedCombinationDefaults].some(
      ([id]) => !combinations.some((item) => item.id === id),
    )
  ) {
    throw new Error('系统快捷组合不可被损坏或删除');
  }
  const persistedShareFieldCombinations = (
    state.catalog as Partial<CpqState['catalog']>
  ).shareFieldCombinations;
  const shareFieldCombinations =
    persistedShareFieldCombinations === undefined
      ? createShareFieldCombinations()
      : validateShareFieldCombinations(state, persistedShareFieldCombinations);
  const permissionGovernance = hydratePermissionGovernance(
    (state.catalog as Partial<typeof state.catalog>).permissionGovernance,
    roles,
    users,
    definitions,
  );
  const quotes = Object.fromEntries(
    Object.entries(state.quotes).map(([quoteId, quote]) => {
      const legacy = quote as Quote & { businessMode?: BusinessModeCode };
      const businessModeCode = legacy.businessModeCode ?? legacy.businessMode;
      if (businessModeCode === undefined) {
        throw new Error(`报价 ${quoteId} 缺少业务模式来源事实`);
      }
      if (quote.customer.customerNo) {
        const customer = customers.find((item) =>
          quote.customer.customerId
            ? item.id === quote.customer.customerId
            : sourceVersion === 5
              ? false
              : item.customerNo === quote.customer.customerNo,
        );
        const legacyCustomerBinding =
          customer &&
          (!quote.customer.customerId || legacyPermissionResourceMissing)
            ? {
                ...quote.customer,
                customerId: customer.id,
                managerUserId: customer.managerUserId,
              }
            : quote.customer;
        if (
          !customer ||
          legacyCustomerBinding.customerId !== customer.id ||
          legacyCustomerBinding.managerUserId !== customer.managerUserId
        ) {
          throw new Error(`报价 ${quoteId} 的客户资源归属无效`);
        }
        quote = { ...quote, customer: legacyCustomerBinding };
      }
      const definitionMap = new Map(
        definitions.map((field) => [field.key, field]),
      );
      if (
        sourceVersion === 5 &&
        [
          ...quote.customColumns,
          ...(quote.fieldSelections ?? []).map(
            (selection) => selection.fieldKey,
          ),
          ...(quote.customColumnSnapshot ?? []).map((field) => field.key),
        ].some((key) => removedDefinitionKeys.has(key))
      ) {
        throw new Error(`报价 ${quoteId} 包含已删除脏字段`);
      }
      if (
        sourceVersion === 5 &&
        [
          ...quote.customColumns,
          ...(quote.fieldSelections ?? []).map(
            (selection) => selection.fieldKey,
          ),
          ...(quote.customColumnSnapshot ?? []).map((field) => field.key),
        ].some((key) => FIXED_QUOTE_HEADER_FIELD_KEYS.includes(key as never))
      ) {
        throw new Error(`报价 ${quoteId} 的固定抬头字段误入字段选择`);
      }
      const hasExplicitSelections = Array.isArray(quote.fieldSelections);
      const rawSelections = hasExplicitSelections
        ? (quote.fieldSelections ?? [])
        : quote.customColumns
            .filter(
              (fieldKey) =>
                quoteLinePoolKeys.has(fieldKey) && knownKeys.has(fieldKey),
            )
            .map((fieldKey) => ({
              fieldKey,
              placement: 'quote-line' as const,
            }));
      if (
        new Set(rawSelections.map((selection) => selection.fieldKey)).size !==
        rawSelections.length
      ) {
        throw new Error(`报价 ${quoteId} 的字段选择落点无效`);
      }
      const normalizedSelections = rawSelections.flatMap((selection) => {
        if (removedDefinitionKeys.has(selection.fieldKey)) {
          if (sourceVersion === 5)
            throw new Error(`报价 ${quoteId} 包含已删除脏字段`);
          return [];
        }
        if (retiredDeviceDefinitionKeys.has(selection.fieldKey)) {
          // Current drafts drop retired device fields. Published/history
          // quotes keep their original selection metadata unchanged; their
          // frozen column snapshot is the compatibility boundary.
          return quote.status === 'draft'
            ? []
            : [
                {
                  fieldKey: selection.fieldKey,
                  placement: selection.placement,
                },
              ];
        }
        if (
          selection.placement === 'quote-header' &&
          FIXED_QUOTE_HEADER_FIELD_KEYS.includes(selection.fieldKey as never)
        ) {
          if (sourceVersion === 5)
            throw new Error(`报价 ${quoteId} 的固定抬头字段误入字段选择`);
          // Header facts are fixed quote metadata, not quote-pool selections.
          // Legacy v4 selections are migrated out without duplicating their
          // definitions or invalidating the historical quote.
          return [];
        }
        const entry = pool.find(
          (candidate) => candidate.fieldKey === selection.fieldKey,
        );
        if (!entry?.enabled || !knownKeys.has(selection.fieldKey)) {
          throw new Error(`报价 ${quoteId} 的字段选择落点无效`);
        }
        return [{ fieldKey: selection.fieldKey, placement: entry.placement }];
      });
      const requiredSelections =
        quote.status === 'draft'
          ? pool
              .filter(
                (entry) =>
                  entry.enabled &&
                  (entry.mandatory ||
                    (sourceVersion < 5 && entry.defaultVisible)),
              )
              .map((entry) => ({
                fieldKey: entry.fieldKey,
                placement: entry.placement,
              }))
          : [];
      /** Selection is a quote fact, not a role projection. Changing the
       * current actor must not silently delete selected fields. */
      const fieldSelections = [...normalizedSelections];
      for (const required of requiredSelections) {
        if (
          !fieldSelections.some(
            (selection) => selection.fieldKey === required.fieldKey,
          )
        ) {
          fieldSelections.push(required);
        }
      }
      const customColumns =
        quote.status === 'draft'
          ? fieldSelections
              .filter((selection) => selection.placement === 'quote-line')
              .map((selection) => selection.fieldKey)
          : [
              ...new Set(
                quote.customColumns.filter(
                  (key) => !removedDefinitionKeys.has(key),
                ),
              ),
            ];
      const hasExplicitSnapshot = Array.isArray(quote.customColumnSnapshot);
      const retainedSnapshots = hasExplicitSnapshot
        ? (quote.customColumnSnapshot ?? []).filter(
            (field) => !removedDefinitionKeys.has(field.key),
          )
        : [];
      if (quote.status !== 'draft') {
        for (const field of retainedSnapshots) {
          if (
            !retiredDeviceDefinitionKeys.has(field.key) &&
            !isExplicitLegacySnapshot(field)
          )
            assertModernSnapshot(field, definitionMap);
        }
      }
      const customColumnSnapshot =
        quote.status === 'draft'
          ? fieldSelections.map((selection) => {
              const field = definitionMap.get(selection.fieldKey);
              const entry = pool.find(
                (candidate) => candidate.fieldKey === selection.fieldKey,
              );
              if (!field || !entry)
                throw new Error(`报价 ${quoteId} 的字段快照来源无效`);
              const retained = retainedSnapshots.find(
                (snapshot) =>
                  snapshot.key === selection.fieldKey &&
                  (snapshot.placement ?? 'quote-line') === selection.placement,
              );
              const retainedCalculation = retained?.calculation
                ? retained
                : undefined;
              return {
                key: field.key,
                label: retainedCalculation
                  ? retainedCalculation.label
                  : entry.displayLabel,
                type: retainedCalculation
                  ? retainedCalculation.type
                  : field.type,
                width: retainedCalculation
                  ? retainedCalculation.width
                  : field.width,
                note: retainedCalculation
                  ? retainedCalculation.note
                  : field.note,
                ...((
                  retainedCalculation
                    ? retainedCalculation.options
                    : field.options
                )
                  ? {
                      options: (retainedCalculation
                        ? retainedCalculation.options
                        : field.options
                      )?.map((option) => ({ ...option })),
                    }
                  : {}),
                sourceKind: retainedCalculation
                  ? retainedCalculation.sourceKind
                  : field.sourceKind,
                fieldKey: retainedCalculation
                  ? retainedCalculation.fieldKey
                  : field.governance.fieldKey,
                definitionVersion: retainedCalculation
                  ? retainedCalculation.definitionVersion
                  : field.governance.definitionVersion,
                formulaVersion: retainedCalculation
                  ? retainedCalculation.formulaVersion
                  : field.governance.formulaVersion,
                ...(retainedCalculation
                  ? {
                      calculation: structuredClone(
                        retainedCalculation.calculation,
                      ),
                    }
                  : field.calculation
                    ? { calculation: structuredClone(field.calculation) }
                    : {}),
                ...(retained?.calculationSnapshot
                  ? {
                      calculationSnapshot: structuredClone(
                        retained.calculationSnapshot,
                      ),
                    }
                  : {}),
                placement: selection.placement,
              };
            })
          : hasExplicitSnapshot
            ? uniqueByKey(
                retainedSnapshots.map((field) => ({
                  ...field,
                  placement: field.placement ?? 'quote-line',
                })),
              )
            : legacySnapshot(quote);
      if (
        quote.status === 'draft' &&
        fieldSelections.some(
          (selection) =>
            !customColumnSnapshot?.some(
              (field) =>
                field.key === selection.fieldKey &&
                (field.placement ?? 'quote-line') === selection.placement &&
                !isExplicitLegacySnapshot(field),
            ),
        )
      )
        throw new Error('草稿报价缺少当前字段版本快照');
      let hydratedQuote: Quote = {
        ...quote,
        items: quote.items.map((item) => {
          const legacyInput = !item.pricingBaseSnapshot;
          const normalized = legacyInput
            ? {
                ...item,
                pricingBaseSnapshot: createPricingBaseSnapshot(
                  businessModeCode,
                  {
                    rentalPrice:
                      item.deviceFieldSnapshot?.rentalPrice ?? item.unitPrice,
                    retailPriceTax13:
                      item.deviceFieldSnapshot?.retailPriceTax13 ??
                      item.unitPrice,
                    taxInclusivePlanPrice:
                      item.deviceFieldSnapshot?.taxInclusivePlanPrice,
                    packageItemPrice: item.packageItemPriceSnapshot,
                    legacyUnitPrice: item.unitPrice,
                  },
                ),
              }
            : item;
          return quarantineLegacyManualValues(
            normalized,
            definitionMap,
            legacyInput,
          );
        }),
        businessModeCode,
        businessModeLabelSnapshot:
          legacy.businessModeLabelSnapshot ??
          businessModeLabel(businessModes, businessModeCode),
        businessModeRequiresReselection:
          legacy.businessModeRequiresReselection ??
          (quote.status === 'draft' &&
            !isStableBusinessModeCode(businessModeCode)),
        customColumns,
        fieldSelections,
        customColumnSnapshot,
      };
      const persistedBindingCount = Object.keys(
        quote.calculationBindings ?? {},
      ).length;
      const persistedCalculationSnapshot = retainedSnapshots.some((snapshot) =>
        Boolean(snapshot.calculation),
      );
      if (
        quote.status === 'draft' &&
        persistedBindingCount === 0 &&
        !persistedCalculationSnapshot &&
        customColumnSnapshot?.some((snapshot) => snapshot.calculation)
      ) {
        // One-time v5 compatibility for drafts written before calculation
        // bindings existed: their old snapshot contains no formula fact, so
        // binding the canonical definition now cannot silently replace a
        // previous version. Modern drafts that already carry a calculation
        // snapshot must provide their matching binding and fail closed later.
        hydratedQuote = bindLatestSelectedCalculations(
          {
            ...state,
            catalog: {
              ...state.catalog,
              quoteFieldDefinitions: definitions,
              quoteFieldPool: pool,
            },
          },
          hydratedQuote,
        );
      }
      delete (
        hydratedQuote as typeof hydratedQuote & { businessMode?: unknown }
      ).businessMode;
      // Order-confirmation PII is session-only until the formal adapter is
      // injected. Never restore PR #69's raw browser-storage form payload.
      delete hydratedQuote.orderConfirmation;
      delete (hydratedQuote as typeof hydratedQuote & { orderForm?: unknown })
        .orderForm;
      return [quoteId, hydratedQuote];
    }),
  );
  const receipts = state.receipts.map((receipt) => ({
    ...receipt,
    selectionSnapshot: receipt.selectionSnapshot ?? {
      kind: 'legacy-unrecoverable' as const,
      reason: '旧 v4 未保存结构化客户选择，不能从 note 恢复',
    },
  }));
  const hydratedPackages = hydratePackageRelations(
    state.catalog.packages,
    new Set(state.catalog.devices.map((device) => device.id)),
    new Set(state.catalog.plans.map((plan) => plan.id)),
  );
  const hydratedPlans = state.catalog.plans.map((plan) => {
    const {
      productPrices: _retiredProductPrices,
      packagePrices: _retiredPackagePrices,
      ...stablePlan
    } = plan as PlanCatalogRecord & {
      productPrices?: unknown;
      packagePrices?: unknown;
    };
    const images = Array.isArray(stablePlan.images)
      ? stablePlan.images.filter((item) => typeof item === 'string' && item)
      : stablePlan.image
        ? [stablePlan.image]
        : [];
    return { ...stablePlan, image: images[0] ?? '', images };
  });
  const shareStyles = hydrateShareStyles(
    (state as Partial<CpqState>).shareStyles,
    sourceVersion,
  );
  const hydrated: CpqState = {
    ...state,
    currentRoleId,
    shareStyles,
    negotiationDrafts: Array.isArray(state.negotiationDrafts)
      ? state.negotiationDrafts.filter((draft) => {
          if (!draft || typeof draft !== 'object') return false;
          const publication = state.publications?.find(
            (item) => item.id === draft.publicationId,
          );
          return Boolean(
            publication &&
              publication.linkId === draft.linkId &&
              typeof draft.id === 'string' &&
              typeof draft.companyOrName === 'string' &&
              typeof draft.phone === 'string' &&
              typeof draft.note === 'string' &&
              Array.isArray(draft.items) &&
              draft.items.every(hasStablePublicShareItem) &&
              hasIsoTimestamp(draft.updatedAt),
          );
        })
      : [],
    currentActorUserId: validatedActorUserId,
    quotes: hydrateReceiptSources(quotes, receipts, state.shares),
    receipts,
    exportTasks: hydrateExportTasks(state, shareStyles),
    stockReminders: hydrateStockReminders(state),
    catalog: {
      ...state.catalog,
      domainSchemaVersion: 3,
      customers,
      roles,
      users,
      businessModes,
      settings,
      departmentCompanyMappings,
      packages: hydratedPackages,
      plans: hydratedPlans,
      devices: state.catalog.devices.map((device) => {
        const legacy = device as Partial<DeviceCatalogRecord>;
        const withTime =
          legacy.updatedAt !== undefined || legacy.updatedAtSource !== undefined
            ? device
            : {
                ...device,
                updatedAt: LEGACY_DEVICE_UPDATED_AT,
                updatedAtSource: 'legacy-v4-fixed-baseline' as const,
              };
        return hydrateDeviceCatalogRecord(withTime);
      }),
      quoteFieldDefinitions: definitions,
      quoteFieldPool: pool,
      quoteFieldCombinations: combinations,
      shareFieldCombinations,
      quoteFieldPresets: presets,
      permissionGovernance,
    },
  };
  if (!validateCustomFieldValuesInState(hydrated))
    throw new Error('企业自定义字段值无效');
  return guardRoleView(hydrated);
}

function hasIsoTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    return new Date(value).toISOString() === value;
  } catch {
    return false;
  }
}

const STABLE_SOURCE_TYPES: QuoteSourceType[] = [
  'plan',
  'package',
  'device',
  'temporary',
  'receipt',
  'rental',
  'legacy',
];

function hasStableQuoteItem(record: unknown): record is QuoteItem {
  if (!record || typeof record !== 'object') return false;
  const item = record as Partial<QuoteItem>;
  const relationValues = [
    item.packageRelationId,
    item.packageId,
    item.packageItemPriceSnapshot,
    item.packageRelationQuantitySnapshot,
  ];
  const relationSnapshotValid =
    relationValues.every((value) => value === undefined) ||
    (typeof item.packageRelationId === 'string' &&
      item.packageRelationId.trim().length > 0 &&
      typeof item.packageId === 'string' &&
      item.packageId.trim().length > 0 &&
      Number.isFinite(item.packageItemPriceSnapshot) &&
      Number(item.packageItemPriceSnapshot) >= 0 &&
      Number.isInteger(item.packageRelationQuantitySnapshot) &&
      Number(item.packageRelationQuantitySnapshot) > 0);
  const planSnapshotValid = item.formalPlanOccurrence
    ? typeof item.planId === 'string' &&
      item.planId.trim().length > 0 &&
      item.planDiscountSnapshot === undefined
    : (item.planId === undefined && item.planDiscountSnapshot === undefined) ||
      (typeof item.planId === 'string' &&
        item.planId.trim().length > 0 &&
        Number.isFinite(item.planDiscountSnapshot) &&
        Number(item.planDiscountSnapshot) >= 0);
  const deviceSnapshot = item.deviceFieldSnapshot;
  const deviceSnapshotValid =
    deviceSnapshot === undefined ||
    ((deviceSnapshot.productAttribute === undefined ||
      typeof deviceSnapshot.productAttribute === 'string') &&
      (deviceSnapshot.deviceAttribute === undefined ||
        typeof deviceSnapshot.deviceAttribute === 'string') &&
      (deviceSnapshot.procurementAttribute === undefined ||
        ['可以采购', '不可采购', '暂停采购'].includes(
          deviceSnapshot.procurementAttribute,
        )) &&
      (deviceSnapshot.weight == null ||
        (Number.isFinite(deviceSnapshot.weight) &&
          deviceSnapshot.weight >= 0)) &&
      typeof deviceSnapshot.jkyCode === 'string' &&
      [deviceSnapshot.retailPriceTax13, deviceSnapshot.dynamicCost].every(
        (value) => value === null || (Number.isFinite(value) && value >= 0),
      ));
  const deviceMasterPricesValid =
    deviceSnapshot === undefined ||
    [
      deviceSnapshot.unitPrice,
      deviceSnapshot.untaxedPlanPrice,
      deviceSnapshot.compensationPrice,
      deviceSnapshot.buyoutFinalPriceTax13,
      deviceSnapshot.purchaseFinalPriceExTax13,
    ].every((value) => value == null || (Number.isFinite(value) && value >= 0));
  const manualFieldValuesValid =
    item.manualFieldValues === undefined ||
    (item.manualFieldValues !== null &&
      typeof item.manualFieldValues === 'object' &&
      Object.entries(item.manualFieldValues).every(
        ([key, value]) =>
          !isDeviceMasterQuoteField(key) &&
          (typeof value === 'string' ||
            typeof value === 'boolean' ||
            Number.isFinite(value) ||
            (Array.isArray(value) &&
              value.every((item) => typeof item === 'string'))),
      ));
  const sourceFieldValueSnapshotValid =
    item.sourceFieldValueSnapshot === undefined ||
    (item.sourceFieldValueSnapshot !== null &&
      typeof item.sourceFieldValueSnapshot === 'object' &&
      Object.values(item.sourceFieldValueSnapshot).every(
        (value) =>
          value === null ||
          typeof value === 'string' ||
          typeof value === 'boolean' ||
          Number.isFinite(value) ||
          (Array.isArray(value) &&
            value.every((entry) => typeof entry === 'string')),
      ));
  const calculationSnapshotsValid =
    item.calculationSnapshots === undefined ||
    (item.calculationSnapshots !== null &&
      typeof item.calculationSnapshots === 'object' &&
      Object.values(item.calculationSnapshots).every(
        (snapshot) =>
          snapshot !== null &&
          typeof snapshot === 'object' &&
          isSafeCalculationVersion(
            (snapshot as { definitionVersion?: unknown }).definitionVersion,
          ) &&
          Number.isFinite((snapshot as { result?: unknown }).result) &&
          Array.isArray((snapshot as { factors?: unknown }).factors) &&
          (snapshot as { factors: unknown[] }).factors.every(
            (factor) =>
              factor !== null &&
              typeof factor === 'object' &&
              typeof (factor as { fieldKey?: unknown }).fieldKey === 'string' &&
              Number.isFinite((factor as { value?: unknown }).value) &&
              ['number', 'money', 'percent'].includes(
                String((factor as { valueType?: unknown }).valueType),
              ) &&
              typeof (factor as { quoteId?: unknown }).quoteId === 'string' &&
              Number.isInteger(
                (factor as { quoteVersion?: unknown }).quoteVersion,
              ) &&
              typeof (factor as { owner?: unknown }).owner === 'string' &&
              typeof (factor as { scopeId?: unknown }).scopeId === 'string',
          ),
      ));
  return (
    typeof item.itemId === 'string' &&
    item.itemId.trim().length > 0 &&
    typeof item.deviceId === 'string' &&
    item.deviceId.trim().length > 0 &&
    typeof item.name === 'string' &&
    item.name.trim().length > 0 &&
    typeof item.sku === 'string' &&
    item.sku.trim().length > 0 &&
    STABLE_SOURCE_TYPES.includes(item.sourceType as QuoteSourceType) &&
    typeof item.sourceId === 'string' &&
    item.sourceId.trim().length > 0 &&
    typeof item.sourceName === 'string' &&
    item.sourceName.trim().length > 0 &&
    Number.isFinite(item.quantity) &&
    Number(item.quantity) > 0 &&
    Number.isFinite(item.unitPrice) &&
    Number(item.unitPrice) >= 0 &&
    Number.isFinite(item.discount) &&
    Number(item.discount) >= 0 &&
    Number.isFinite(item.stock) &&
    relationSnapshotValid &&
    planSnapshotValid &&
    deviceSnapshotValid &&
    deviceMasterPricesValid &&
    manualFieldValuesValid &&
    sourceFieldValueSnapshotValid &&
    calculationSnapshotsValid &&
    (item.replacementDeviceId === undefined ||
      (typeof item.replacementDeviceId === 'string' &&
        item.replacementDeviceId.trim().length > 0)) &&
    (item.replacementRelationId === undefined ||
      (typeof item.replacementRelationId === 'string' &&
        item.replacementRelationId.trim().length > 0)) &&
    (item.replacementPoolId === undefined ||
      item.replacementPoolId === null ||
      (typeof item.replacementPoolId === 'string' &&
        item.replacementPoolId.trim().length > 0))
  );
}

function hasStableQuoteCalculationFacts(quote: Quote): boolean {
  const bindings = quote.calculationBindings ?? {};
  const selected = new Set(
    [
      ...quote.customColumns.map((fieldKey) => ({
        fieldKey,
        placement: 'quote-line' as const,
      })),
      ...(quote.fieldSelections ?? []),
    ].map(({ fieldKey, placement }) => `${placement}:${fieldKey}`),
  );
  if (
    Object.entries(bindings).some(
      ([fieldKey, binding]) =>
        binding.fieldKey !== fieldKey ||
        binding.definitionVersion !== binding.definition.definitionVersion ||
        !isSafeCalculationVersion(binding.definitionVersion) ||
        Object.values(binding.fieldTypes).some(
          (type) => !['number', 'money', 'percent'].includes(type),
        ),
    )
  )
    return false;
  const reachableBindings = new Set<string>();
  const visitBinding = (
    fieldKey: string,
    stack: readonly string[],
  ): boolean => {
    if (stack.includes(fieldKey)) return false;
    const binding = bindings[fieldKey];
    if (!binding || reachableBindings.has(fieldKey)) return true;
    reachableBindings.add(fieldKey);
    return binding.definition.operands.every(
      (operand) =>
        operand.kind !== 'field' ||
        !bindings[operand.fieldKey] ||
        visitBinding(operand.fieldKey, [...stack, fieldKey]),
    );
  };
  for (const identity of selected) {
    const fieldKey = identity.slice(identity.indexOf(':') + 1);
    if (!visitBinding(fieldKey, [])) return false;
  }
  if (
    Object.keys(bindings).some((fieldKey) => !reachableBindings.has(fieldKey))
  )
    return false;
  for (const item of quote.items) {
    for (const [fieldKey, fact] of Object.entries(
      item.calculationSnapshots ?? {},
    )) {
      if (
        fact.fieldKey !== fieldKey ||
        !selected.has(`${fact.placement}:${fieldKey}`) ||
        fact.quoteId !== quote.quoteId ||
        fact.quoteVersion !== quote.version ||
        fact.itemId !== item.itemId ||
        fact.definitionVersion !== fact.definition.definitionVersion ||
        !isSafeCalculationVersion(fact.definitionVersion) ||
        !Number.isFinite(fact.result) ||
        !['quote-line', 'package-summary', 'quote-header'].includes(
          fact.placement,
        ) ||
        !fact.scopeId?.trim() ||
        !Array.isArray(fact.factors) ||
        fact.factors.some(
          (factor) =>
            !factor.fieldKey?.trim() ||
            !Number.isFinite(factor.value) ||
            !['number', 'money', 'percent'].includes(factor.valueType) ||
            factor.quoteId !== quote.quoteId ||
            factor.quoteVersion !== quote.version ||
            factor.owner !== fact.definition.owner ||
            factor.scopeId !== fact.scopeId ||
            (Boolean(fact.definition.resolverRef) &&
              fact.placement === 'package-summary' &&
              factor.itemId === undefined) ||
            (factor.itemId !== undefined &&
              !quote.items.some(
                (candidate) => candidate.itemId === factor.itemId,
              )) ||
            (fact.placement === 'package-summary' &&
              factor.itemId !== undefined &&
              !quote.items.some(
                (candidate) =>
                  candidate.itemId === factor.itemId &&
                  (candidate.packageId === fact.scopeId ||
                    candidate.planId === fact.scopeId),
              )) ||
            (factor.relationId !== undefined &&
              !quote.items.some(
                (candidate) =>
                  candidate.packageRelationId === factor.relationId &&
                  (!factor.itemId || candidate.itemId === factor.itemId),
              )),
        ) ||
        new Set(
          fact.factors.map(
            (factor) =>
              `${factor.fieldKey}:${factor.scopeId}:${factor.itemId ?? ''}:${factor.relationId ?? ''}`,
          ),
        ).size !== fact.factors.length
      )
        return false;
      const binding = bindings[fieldKey];
      if (
        !binding ||
        binding.definitionVersion !== fact.definitionVersion ||
        JSON.stringify(binding.definition) !== JSON.stringify(fact.definition)
      )
        return false;
      const auditKeys = new Set<string>();
      const auditBindings = new Set<string>();
      const collectAudit = (key: string, stack: readonly string[]): boolean => {
        if (stack.includes(key)) return false;
        const current = bindings[key];
        if (!current || auditBindings.has(key)) return true;
        auditBindings.add(key);
        for (const factorKey of calculationAuditFactorKeys(current.definition))
          auditKeys.add(factorKey);
        return current.definition.operands.every(
          (operand) =>
            operand.kind !== 'field' ||
            !bindings[operand.fieldKey] ||
            collectAudit(operand.fieldKey, [...stack, key]),
        );
      };
      if (
        !collectAudit(fieldKey, []) ||
        fact.factors.some((factor) => !auditKeys.has(factor.fieldKey))
      )
        return false;
      // Device rows expose package_discounted_subtotal as the immutable
      // final line amount used by the quote totals. It is a direct projection
      // rather than the package formula, so its frozen fact deliberately has
      // no audit factors and cannot be replayed through the package resolver.
      if (
        fieldKey === 'package_discounted_subtotal' &&
        item.sourceType === 'device' &&
        fact.factors.length === 0
      )
        continue;
      if (fact.placement === 'quote-line' && fact.scopeId !== item.itemId)
        return false;
      if (
        fact.placement === 'package-summary' &&
        fact.scopeId !== item.packageId &&
        fact.scopeId !== item.planId
      )
        return false;
      const replayBound = (
        key: string,
        stack: readonly string[],
      ): ReturnType<typeof replayCalculationFromFactors> => {
        if (stack.includes(key))
          return { ok: false, error: '冻结计算依赖循环' };
        const current = bindings[key];
        if (!current) return { ok: false, error: `冻结计算绑定缺失：${key}` };
        if (current.definition.resolverRef)
          return replayCalculationFromFactors(
            current.definition,
            fact.factors,
            current.fieldTypes,
          );
        for (const operand of current.definition.operands) {
          if (operand.kind !== 'field' || !bindings[operand.fieldKey]) continue;
          const upstream = replayBound(operand.fieldKey, [...stack, key]);
          const frozenUpstream = fact.factors.filter(
            (factor) => factor.fieldKey === operand.fieldKey,
          );
          if (
            !upstream.ok ||
            frozenUpstream.length !== 1 ||
            frozenUpstream[0].value !== upstream.value
          )
            return {
              ok: false,
              error: `冻结上游计算不一致：${operand.fieldKey}`,
            };
        }
        return replayCalculationFromFactors(
          current.definition,
          fact.factors,
          current.fieldTypes,
        );
      };
      const evaluated = replayBound(fieldKey, []);
      if (!evaluated.ok || evaluated.value !== fact.result) return false;
    }
  }
  return true;
}

function hasStablePublicShareItem(record: unknown): boolean {
  if (!record || typeof record !== 'object') return false;
  const item = record as Partial<PublicShareItemSnapshot> & {
    manualFieldValues?: unknown;
  };
  return (
    typeof item.itemId === 'string' &&
    item.itemId.trim().length > 0 &&
    typeof item.name === 'string' &&
    item.name.trim().length > 0 &&
    typeof item.sku === 'string' &&
    item.sku.trim().length > 0 &&
    Number.isFinite(item.quantity) &&
    Number(item.quantity) > 0 &&
    Number.isFinite(item.unitPrice) &&
    Number(item.unitPrice) >= 0 &&
    Number.isFinite(item.discount) &&
    Number(item.discount) >= 0 &&
    item.manualFieldValues === undefined &&
    (item.fieldValues === undefined ||
      (item.fieldValues !== null &&
        typeof item.fieldValues === 'object' &&
        Object.entries(item.fieldValues).every(
          ([fieldId, value]) =>
            typeof fieldId === 'string' &&
            fieldId.trim().length > 0 &&
            (value === null ||
              typeof value === 'string' ||
              (typeof value === 'number' && Number.isFinite(value)) ||
              typeof value === 'boolean' ||
              (Array.isArray(value) &&
                value.every((entry) => typeof entry === 'string'))),
        ))) &&
    (item.deviceId === undefined ||
      (typeof item.deviceId === 'string' && item.deviceId.trim().length > 0)) &&
    (item.stock === undefined ||
      (Number.isFinite(item.stock) && Number(item.stock) >= 0)) &&
    (item.sourceType === undefined ||
      STABLE_SOURCE_TYPES.includes(item.sourceType)) &&
    (item.sourceId === undefined ||
      (typeof item.sourceId === 'string' && item.sourceId.trim().length > 0)) &&
    (item.sourceName === undefined ||
      (typeof item.sourceName === 'string' &&
        item.sourceName.trim().length > 0)) &&
    (item.replacementDeviceId === undefined ||
      (typeof item.replacementDeviceId === 'string' &&
        item.replacementDeviceId.trim().length > 0)) &&
    (item.remark === undefined || typeof item.remark === 'string')
  );
}

function hasShareFieldCombinationSnapshot(
  value: unknown,
): value is ShareFieldCombinationSnapshot {
  if (!value || typeof value !== 'object') return false;
  const snapshot = value as Partial<ShareFieldCombinationSnapshot>;
  const fieldIds = snapshot.fieldIds;
  const selectedFieldIds = snapshot.selectedFieldIds;
  const fields = snapshot.fields;
  if (!Array.isArray(fieldIds) || !Array.isArray(fields)) return false;
  return (
    typeof snapshot.id === 'string' &&
    snapshot.id.trim().length > 0 &&
    typeof snapshot.name === 'string' &&
    snapshot.name.trim().length > 0 &&
    fieldIds.length > 0 &&
    new Set(fieldIds).size === fieldIds.length &&
    fieldIds.every(
      (fieldId) => typeof fieldId === 'string' && fieldId.trim().length > 0,
    ) &&
    Array.isArray(snapshot.channels) &&
    snapshot.channels.length > 0 &&
    new Set(snapshot.channels).size === snapshot.channels.length &&
    snapshot.channels.every((channel) =>
      [
        'channel:internal-share',
        'channel:public-link',
        'channel:pdf',
        'channel:excel',
        'channel:document',
      ].includes(channel),
    ) &&
    fields.length === fieldIds.length &&
    (selectedFieldIds === undefined ||
      (Array.isArray(selectedFieldIds) &&
        selectedFieldIds.length > 0 &&
        new Set(selectedFieldIds).size === selectedFieldIds.length &&
        selectedFieldIds.every(
          (fieldId) => typeof fieldId === 'string' && fieldId.trim().length > 0,
        ))) &&
    fields.every(
      (field) =>
        !!field &&
        typeof field === 'object' &&
        typeof field.fieldId === 'string' &&
        field.fieldId.trim().length > 0 &&
        typeof field.label === 'string' &&
        field.label.trim().length > 0 &&
        typeof field.type === 'string' &&
        typeof field.width === 'string' &&
        (field.width === 'normal' || field.width === 'long') &&
        (field.sourceKind === 'system' || field.sourceKind === 'manual'),
    ) &&
    fields.every((field) => fieldIds.includes(field.fieldId))
  );
}

function hasQuoteOutputProjectionSnapshot(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const snapshot = value as Partial<ExportTaskRecord['projectionSnapshot']>;
  const fieldIds = snapshot.fieldIds;
  const columns = snapshot.columns;
  const rows = snapshot.rows;
  if (
    !Array.isArray(fieldIds) ||
    !Array.isArray(columns) ||
    !Array.isArray(rows)
  )
    return false;
  return (
    typeof snapshot.channelId === 'string' &&
    [
      'channel:internal-share',
      'channel:public-link',
      'channel:pdf',
      'channel:excel',
      'channel:document',
    ].includes(snapshot.channelId) &&
    fieldIds.length > 0 &&
    new Set(fieldIds).size === fieldIds.length &&
    fieldIds.every(
      (fieldId) => typeof fieldId === 'string' && fieldId.trim().length > 0,
    ) &&
    columns.every(
      (column) =>
        !!column &&
        typeof column.fieldId === 'string' &&
        column.fieldId.trim().length > 0 &&
        fieldIds.includes(column.fieldId) &&
        typeof column.label === 'string' &&
        column.label.trim().length > 0,
    ) &&
    rows.every(
      (row) =>
        !!row &&
        typeof row === 'object' &&
        !!row.values &&
        typeof row.values === 'object',
    ) &&
    (snapshot.businessSections === undefined ||
      hasQuoteOutputBusinessSectionsSnapshot(snapshot.businessSections))
  );
}

function hasQuoteOutputBusinessSectionsSnapshot(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every((section) => {
      if (!section || typeof section !== 'object') return false;
      const candidate = section as { kind?: unknown; values?: unknown };
      return (
        (candidate.kind === 'rental-terms' ||
          candidate.kind === 'purchase-terms') &&
        !!candidate.values &&
        typeof candidate.values === 'object' &&
        !Array.isArray(candidate.values) &&
        Object.values(candidate.values).every(
          (entry) =>
            entry === null ||
            typeof entry === 'string' ||
            (typeof entry === 'number' && Number.isFinite(entry)),
        )
      );
    })
  );
}

function hydrateExportTasks(
  state: CpqState,
  shareStyles = state.shareStyles,
): ExportTaskRecord[] {
  const raw = (state as Partial<CpqState>).exportTasks;
  if (raw === undefined) return [];
  if (
    !Array.isArray(raw) ||
    raw.some(
      (task) =>
        !task ||
        typeof task !== 'object' ||
        typeof task.id !== 'string' ||
        !task.id.trim() ||
        typeof task.quoteId !== 'string' ||
        !task.quoteId.trim() ||
        !Number.isInteger(task.quoteVersion) ||
        task.quoteVersion < 1 ||
        task.status !== 'completed' ||
        !hasShareFieldCombinationSnapshot(task.combinationSnapshot) ||
        !isKnownShareStyleSnapshot({ shareStyles }, task.shareStyleSnapshot) ||
        !hasQuoteOutputProjectionSnapshot(task.projectionSnapshot) ||
        !hasIsoTimestamp(task.createdAt),
    )
  )
    throw new Error('导出任务冻结快照无效');
  return structuredClone(raw);
}

function hasStableQuoteItemAdjustment(
  record: unknown,
): record is QuoteItemAdjustmentRecord {
  if (!record || typeof record !== 'object') return false;
  const adjustment = record as Partial<QuoteItemAdjustmentRecord>;
  return (
    typeof adjustment.id === 'string' &&
    adjustment.id.trim().length > 0 &&
    (adjustment.action === 'remove' || adjustment.action === 'replace') &&
    typeof adjustment.itemId === 'string' &&
    adjustment.itemId.trim().length > 0 &&
    hasStableQuoteItem(adjustment.originalItemSnapshot) &&
    (adjustment.effectiveItemSnapshot === null ||
      hasStableQuoteItem(adjustment.effectiveItemSnapshot)) &&
    typeof adjustment.operatorUserId === 'string' &&
    adjustment.operatorUserId.trim().length > 0 &&
    typeof adjustment.reason === 'string' &&
    (adjustment.action === 'replace' || adjustment.reason.trim().length > 0) &&
    hasIsoTimestamp(adjustment.createdAt) &&
    (adjustment.restoredAt === undefined ||
      hasIsoTimestamp(adjustment.restoredAt)) &&
    (adjustment.priceSource === undefined ||
      adjustment.priceSource === 'retained-original' ||
      adjustment.priceSource === 'replacement-device' ||
      adjustment.priceSource === 'manual-adjustment')
  );
}

function hasStableReceipt(record: unknown): record is ReceiptRecord {
  if (!record || typeof record !== 'object') return false;
  const receipt = record as Partial<ReceiptRecord>;
  const snapshot = receipt.selectionSnapshot;
  const snapshotAmounts =
    snapshot?.kind === 'structured' &&
    snapshot.items.every((item) =>
      item.selectionKind === 'removed'
        ? item.effectiveItem === null
        : hasStableQuoteItem(item.effectiveItem),
    )
      ? quoteAmounts({
          items: snapshot.items.flatMap((item) =>
            item.effectiveItem ? [item.effectiveItem] : [],
          ),
          overallDiscount: snapshot.overallDiscountSnapshot,
        })
      : undefined;
  const hasSelectionSnapshot =
    snapshot?.kind === 'legacy-unrecoverable'
      ? typeof snapshot.reason === 'string' && snapshot.reason.trim().length > 0
      : snapshot?.kind === 'structured' &&
        Array.isArray(snapshot.items) &&
        snapshot.items.length > 0 &&
        snapshot.items.every(
          (item) =>
            ['kept', 'added', 'removed', 'replaced'].includes(
              item.selectionKind,
            ) &&
            (item.selectionKind === 'removed'
              ? item.effectiveItem === null
              : hasStableQuoteItem(item.effectiveItem)) &&
            (item.originalItemSnapshot === null ||
              hasStableQuoteItem(item.originalItemSnapshot)) &&
            (item.selectionKind === 'added'
              ? item.originalItemSnapshot === null
              : item.originalItemSnapshot !== null) &&
            (item.selectionKind !== 'replaced' ||
              (typeof item.replacementSource?.requestedDeviceId === 'string' &&
                typeof item.replacementSource.originalDeviceId === 'string' &&
                typeof item.replacementSource.originalItemId === 'string' &&
                item.effectiveItem?.replacementDeviceId === undefined)) &&
            (item.disposition === undefined ||
              ['adopted', 'rejected', 'pending'].includes(item.disposition)),
        ) &&
        Number.isFinite(snapshot.overallDiscountSnapshot) &&
        snapshot.overallDiscountSnapshot >= 0 &&
        Number.isFinite(snapshot.subtotalSnapshot) &&
        Number.isFinite(snapshot.totalSnapshot) &&
        snapshotAmounts?.subtotal === snapshot.subtotalSnapshot &&
        snapshotAmounts.total === snapshot.totalSnapshot &&
        hasIsoTimestamp(snapshot.submittedAt);
  return (
    typeof receipt.id === 'string' &&
    receipt.id.trim().length > 0 &&
    typeof receipt.shareId === 'string' &&
    receipt.shareId.trim().length > 0 &&
    typeof receipt.sourceQuoteId === 'string' &&
    receipt.sourceQuoteId.trim().length > 0 &&
    Number.isInteger(receipt.sourceVersion) &&
    Number(receipt.sourceVersion) > 0 &&
    typeof receipt.companyOrName === 'string' &&
    typeof receipt.phone === 'string' &&
    typeof receipt.note === 'string' &&
    (receipt.publicationId || receipt.linkId || receipt.quoteMasterId
      ? typeof receipt.customerId === 'string' &&
        receipt.customerId.trim().length > 0
      : true) &&
    (receipt.status === 'submitted' || receipt.status === 'converted') &&
    (receipt.convertedQuoteId === undefined ||
      (typeof receipt.convertedQuoteId === 'string' &&
        receipt.convertedQuoteId.trim().length > 0)) &&
    (receipt.status !== 'submitted' ||
      receipt.convertedQuoteId === undefined) &&
    (receipt.openedAt === undefined || hasIsoTimestamp(receipt.openedAt)) &&
    hasIsoTimestamp(receipt.createdAt) &&
    hasSelectionSnapshot
  );
}

function hasStableStockReminder(record: unknown): record is StockReminder {
  if (!record || typeof record !== 'object') return false;
  const reminder = record as Partial<StockReminder>;
  return (
    typeof reminder.id === 'string' &&
    reminder.id.trim().length > 0 &&
    typeof reminder.quoteId === 'string' &&
    reminder.quoteId.trim().length > 0 &&
    typeof reminder.quoteVersionId === 'string' &&
    reminder.quoteVersionId.trim().length > 0 &&
    typeof reminder.quoteItemId === 'string' &&
    reminder.quoteItemId.trim().length > 0 &&
    typeof reminder.skuId === 'string' &&
    reminder.skuId.trim().length > 0 &&
    typeof reminder.deviceId === 'string' &&
    reminder.deviceId.trim().length > 0 &&
    typeof reminder.skuSnapshot === 'string' &&
    reminder.skuSnapshot.trim().length > 0 &&
    (reminder.warehouseId === null ||
      (typeof reminder.warehouseId === 'string' &&
        reminder.warehouseId.trim().length > 0)) &&
    typeof reminder.inventoryRunId === 'string' &&
    reminder.inventoryRunId.trim().length > 0 &&
    (reminder.status === 'OPEN' || reminder.status === 'RESOLVED') &&
    Number.isInteger(reminder.occurrenceCount) &&
    Number(reminder.occurrenceCount) >= 1 &&
    Number.isFinite(reminder.requiredQuantity) &&
    Number(reminder.requiredQuantity) >= 0 &&
    Number.isFinite(reminder.availableQuantity) &&
    Number(reminder.availableQuantity) >= 0 &&
    Number.isInteger(reminder.rowVersion) &&
    Number(reminder.rowVersion) >= 1 &&
    hasIsoTimestamp(reminder.createdAt) &&
    (reminder.resolvedAt === null || hasIsoTimestamp(reminder.resolvedAt)) &&
    (reminder.status !== 'OPEN' || reminder.resolvedAt === null) &&
    ((reminder.latestObservedAt === undefined &&
      reminder.latestObservedRequiredQuantity === undefined &&
      reminder.latestObservedAvailableQuantity === undefined &&
      reminder.latestObservedInventoryRunId === undefined) ||
      (hasIsoTimestamp(reminder.latestObservedAt) &&
        Number.isFinite(reminder.latestObservedRequiredQuantity) &&
        Number(reminder.latestObservedRequiredQuantity) >= 0 &&
        Number.isFinite(reminder.latestObservedAvailableQuantity) &&
        Number(reminder.latestObservedAvailableQuantity) >= 0 &&
        typeof reminder.latestObservedInventoryRunId === 'string' &&
        reminder.latestObservedInventoryRunId.trim().length > 0)) &&
    reminder.projectionSource === 'local-storage-mock'
  );
}

function hasCoreStateShape(value: unknown): value is CpqState | LegacyCpqState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<Omit<CpqState, 'schemaVersion'>> & {
    schemaVersion?: number;
  };
  return (
    (candidate.schemaVersion === 3 ||
      candidate.schemaVersion === 4 ||
      candidate.schemaVersion === 5) &&
    (candidate.currentActorUserId === undefined ||
      typeof candidate.currentActorUserId === 'string') &&
    (candidate.workspacePicker === null ||
      ['plans', 'packages', 'devices', 'temporary'].includes(
        candidate.workspacePicker ?? '',
      )) &&
    typeof candidate.activeQuoteId === 'string' &&
    Array.isArray(candidate.openQuoteIds) &&
    candidate.openQuoteIds.every((id) => typeof id === 'string') &&
    !!candidate.quotes &&
    typeof candidate.quotes === 'object' &&
    !!candidate.quotes[candidate.activeQuoteId] &&
    Array.isArray(candidate.shares) &&
    Array.isArray(candidate.receipts) &&
    Array.isArray(candidate.stockReminders) &&
    Array.isArray(candidate.activities) &&
    Array.isArray(candidate.audits) &&
    Array.isArray(candidate.temporaryDevices) &&
    !!candidate.catalog &&
    Array.isArray(candidate.catalog.warehouses) &&
    candidate.catalog.warehouses.every(
      (warehouse) =>
        typeof warehouse.key === 'string' &&
        warehouse.key.trim().length > 0 &&
        typeof warehouse.name === 'string' &&
        warehouse.name.trim().length > 0,
    ) &&
    new Set(candidate.catalog.warehouses.map((warehouse) => warehouse.key))
      .size === candidate.catalog.warehouses.length &&
    Array.isArray(candidate.catalog.devices) &&
    Array.isArray(candidate.catalog.packages) &&
    Array.isArray(candidate.catalog.plans) &&
    Array.isArray(candidate.catalog.optionCategories) &&
    Array.isArray(candidate.catalog.options) &&
    Array.isArray(candidate.catalog.customers) &&
    Array.isArray(candidate.catalog.users) &&
    Array.isArray(candidate.catalog.roles) &&
    !!candidate.catalog.settings &&
    typeof candidate.catalog.settings === 'object' &&
    !!candidate.stockOverrides &&
    typeof candidate.stockOverrides === 'object' &&
    typeof candidate.stockSyncRevision === 'number' &&
    typeof candidate.nextSequence === 'number'
  );
}

function hasStableSources(state: CpqState | LegacyCpqState): state is CpqState {
  const hasStableDeviceTime = (device: Partial<DeviceCatalogRecord>) => {
    if (
      typeof device.updatedAt !== 'string' ||
      !['fixture', 'user-save', 'legacy-v4-fixed-baseline'].includes(
        device.updatedAtSource ?? '',
      )
    ) {
      return false;
    }
    try {
      return new Date(device.updatedAt).toISOString() === device.updatedAt;
    } catch {
      return false;
    }
  };
  const knownDeviceIds = new Set(
    state.catalog.devices.map((device) => device.id),
  );
  const knownPlanIds = new Set(state.catalog.plans.map((plan) => plan.id));
  const hasStableDepartmentCompanyMappings = (() => {
    if (!Array.isArray(state.catalog.departmentCompanyMappings)) return false;
    try {
      validateDepartmentCompanyMappings(
        state.catalog.departmentCompanyMappings,
        departmentDirectoryFromNames(
          state.catalog.users.map((user) => user.department),
        ),
      );
      return true;
    } catch {
      return false;
    }
  })();
  const hasStableDeviceRelationships = (device: DeviceCatalogRecord) =>
    hasValidDeviceMedia(device) &&
    Array.isArray(device.replacementIds) &&
    new Set(device.replacementIds).size === device.replacementIds.length &&
    device.replacementIds.every(
      (replacementId) =>
        replacementId !== device.id && knownDeviceIds.has(replacementId),
    );
  const hasStableDeviceMasterModel = (device: DeviceCatalogRecord) =>
    (device.procurementAttribute === undefined ||
      ['可以采购', '不可采购', '暂停采购'].includes(
        device.procurementAttribute,
      )) &&
    (device.weight == null ||
      (Number.isFinite(device.weight) && device.weight >= 0)) &&
    (device.packageItemPrice == null ||
      (Number.isFinite(device.packageItemPrice) &&
        device.packageItemPrice >= 0 &&
        device.pricingFieldAvailability?.packageItemPrice === true)) &&
    hasStablePurchasePriceFact(
      device.buyoutFinalPriceTax13,
      device.pricingFieldAvailability?.buyoutFinalPriceTax13,
    ) &&
    hasStablePurchasePriceFact(
      device.purchaseFinalPriceExTax13,
      device.pricingFieldAvailability?.purchaseFinalPriceExTax13,
    ) &&
    [
      device.rentalPrice,
      device.packageItemPrice,
      device.retailPriceTax13,
      device.unitPrice,
      device.untaxedPlanPrice,
      device.dynamicCost,
      device.compensationPrice,
    ].every(
      (value) => value == null || (Number.isFinite(value) && value >= 0),
    ) &&
    DEVICE_MASTER_RETIRED_PROPERTIES.every(
      (property) =>
        !(property in (device as unknown as Record<string, unknown>)),
    );
  const hasStablePackageRelationships = (pkg: PackageCatalogRecord) =>
    Array.isArray(pkg.planIds) &&
    new Set(pkg.planIds).size === pkg.planIds.length &&
    pkg.planIds.every((planId) => knownPlanIds.has(planId)) &&
    Array.isArray(pkg.productLinks) &&
    new Set(pkg.productLinks.map((link) => link.relationId)).size ===
      pkg.productLinks.length &&
    new Set(pkg.productLinks.map((link) => link.deviceId)).size ===
      pkg.productLinks.length &&
    pkg.productLinks.every(
      (link) =>
        link.packageId === pkg.id &&
        knownDeviceIds.has(link.deviceId) &&
        Number.isInteger(link.quantity) &&
        link.quantity > 0 &&
        Number.isInteger(link.sortOrder) &&
        link.sortOrder >= 0 &&
        (link.packageItemPrice === null ||
          (Number.isFinite(link.packageItemPrice) &&
            link.packageItemPrice >= 0)),
    );
  const hasStablePlan = (plan: PlanCatalogRecord) =>
    typeof plan.id === 'string' &&
    plan.id.trim().length > 0 &&
    typeof plan.name === 'string' &&
    plan.name.trim().length > 0 &&
    Number.isFinite(plan.discount) &&
    plan.discount >= 0 &&
    Number.isFinite(plan.sortOrder) &&
    typeof plan.image === 'string' &&
    (plan.images === undefined ||
      (Array.isArray(plan.images) &&
        plan.images.every((item) => typeof item === 'string'))) &&
    Array.isArray(plan.cases) &&
    plan.cases.every((item) => typeof item === 'string');
  const customerIds = new Set<string>();
  const customerNumbers = new Set<string>();
  const hasStableCustomer = (
    customer: CpqState['catalog']['customers'][number],
  ) => {
    if (
      typeof customer.id !== 'string' ||
      !customer.id.trim() ||
      typeof customer.customerNo !== 'string' ||
      !customer.customerNo.trim() ||
      typeof customer.name !== 'string' ||
      !customer.name.trim() ||
      typeof customer.contact !== 'string' ||
      typeof customer.phone !== 'string' ||
      typeof customer.manager !== 'string' ||
      !customer.manager.trim() ||
      typeof customer.managerUserId !== 'string' ||
      !customer.managerUserId.trim() ||
      !['active', 'inactive'].includes(customer.status)
    )
      return false;
    if (
      customerIds.has(customer.id) ||
      customerNumbers.has(customer.customerNo)
    )
      return false;
    customerIds.add(customer.id);
    customerNumbers.add(customer.customerNo);
    return true;
  };
  return (
    state.schemaVersion === 5 &&
    isValidShareStyleCatalog(state.shareStyles) &&
    Array.isArray(state.catalog.businessModes) &&
    state.catalog.businessModes.length === 5 &&
    new Set(state.catalog.businessModes.map((mode) => mode.code)).size === 5 &&
    state.catalog.businessModes.every(
      (mode) =>
        ['rental', 'short_rental', 'full', 'buyout', 'repair'].includes(
          mode.code,
        ) &&
        typeof mode.label === 'string' &&
        mode.label.trim().length > 0 &&
        Number.isFinite(mode.sortOrder) &&
        typeof mode.active === 'boolean',
    ) &&
    state.catalog.devices.every(hasStableDeviceTime) &&
    state.catalog.devices.every(hasStableDeviceMasterModel) &&
    state.catalog.devices.every(hasStableDeviceRelationships) &&
    hasStableDepartmentCompanyMappings &&
    state.catalog.packages.every(hasStablePackageRelationships) &&
    new Set(state.catalog.plans.map((plan) => plan.id)).size ===
      state.catalog.plans.length &&
    state.catalog.plans.every(hasStablePlan) &&
    state.catalog.customers.every(hasStableCustomer) &&
    Object.values(state.quotes).every(
      (quote) =>
        [
          'rental',
          'short_rental',
          'full',
          'buyout',
          'repair',
          'trade',
          'service',
        ].includes(quote.businessModeCode) &&
        typeof quote.businessModeLabelSnapshot === 'string' &&
        quote.businessModeLabelSnapshot.trim().length > 0 &&
        typeof quote.businessModeRequiresReselection === 'boolean' &&
        (quote.outputTermsRequired === undefined ||
          typeof quote.outputTermsRequired === 'boolean') &&
        new Set(quote.items.map((item) => item.itemId)).size ===
          quote.items.length &&
        quote.items.every(hasStableQuoteItem) &&
        hasStableQuoteCalculationFacts(quote) &&
        (quote.itemAdjustments === undefined ||
          (Array.isArray(quote.itemAdjustments) &&
            quote.itemAdjustments.every(hasStableQuoteItemAdjustment))) &&
        (quote.sourceReceiptId === undefined ||
          (typeof quote.sourceReceiptId === 'string' &&
            quote.sourceReceiptId.trim().length > 0)),
    ) &&
    state.receipts.every(hasStableReceipt) &&
    state.stockReminders.every(hasStableStockReminder) &&
    state.shares.every(
      (share) =>
        (share.snapshotPolicy === undefined ||
          share.snapshotPolicy === 'published-channel-v1' ||
          share.snapshotPolicy === 'legacy-unavailable') &&
        (share.businessSectionsSnapshot === undefined ||
          hasQuoteOutputBusinessSectionsSnapshot(
            share.businessSectionsSnapshot,
          )) &&
        (share.itemsSnapshot === undefined ||
          (Array.isArray(share.itemsSnapshot) &&
            share.itemsSnapshot.every(hasStablePublicShareItem))),
    ) &&
    (state.publications ?? []).every(
      (publication) =>
        isKnownShareStyleSnapshot(state, publication.shareStyleSnapshot) &&
        (publication.businessSectionsSnapshot === undefined ||
          hasQuoteOutputBusinessSectionsSnapshot(
            publication.businessSectionsSnapshot,
          )),
    )
  );
}

/** The legacy mapper uses NaN only as the explicit "unknown" purchase-price
 * sentinel. Persisted JSON turns it into null; availability=false restores
 * that sentinel during hydration. Other non-finite values remain invalid. */
function hasStablePurchasePriceFact(
  value: number | null | undefined,
  available: boolean | undefined,
): boolean {
  if (value == null) return available !== true;
  if (Number.isNaN(value)) return available === false;
  return Number.isFinite(value) && value >= 0;
}

function validatePersistablePurchasePrices(
  devices: DeviceCatalogRecord[],
): void {
  for (const device of devices) {
    if (
      !hasStablePurchasePriceFact(
        device.buyoutFinalPriceTax13,
        device.pricingFieldAvailability?.buyoutFinalPriceTax13,
      ) ||
      !hasStablePurchasePriceFact(
        device.purchaseFinalPriceExTax13,
        device.pricingFieldAvailability?.purchaseFinalPriceExTax13,
      )
    ) {
      throw new Error(`设备 ${device.id} 的购买价与可用状态不一致`);
    }
  }
}

function migrateLegacyQuoteItem(item: LegacyQuoteItem): QuoteItem {
  if (item.sourceType && item.sourceId && item.sourceName) {
    const { source: _source, groupName: _groupName, ...stable } = item;
    return stable as QuoteItem;
  }

  const legacyName = item.groupName?.trim() || item.name;
  const directSource = item.source === 'device' || item.source === 'temporary';
  const sourceType = directSource ? item.source : 'legacy';
  const sourceId = directSource
    ? item.deviceId
    : `legacy:${item.source ?? 'unknown'}:${encodeURIComponent(legacyName)}`;
  const sourceName = directSource ? item.name : legacyName;
  const { source: _source, groupName: _groupName, ...rest } = item;
  return { ...rest, sourceType, sourceId, sourceName } as QuoteItem;
}

export function migrateV3State(state: LegacyCpqState): CpqState {
  const quotes = Object.fromEntries(
    Object.entries(state.quotes).map(([quoteId, quote]) => [
      quoteId,
      {
        ...quote,
        items: quote.items.map(migrateLegacyQuoteItem),
      },
    ]),
  );
  const hydrated: CpqState = {
    ...state,
    schemaVersion: 5,
    shareStyles: createDefaultShareStyles(),
    currentRoleId: '',
    // Legacy v3 never persisted a stable member selection. Do not infer one
    // from role order; parsing later supplies the fixed acceptance bootstrap
    // identity, while the tab-scoped port owns every explicit selection.
    currentActorUserId: undefined,
    quotes,
    shares: state.shares.map((share) => {
      const quote = quotes[share.quoteId];
      return quote
        ? {
            ...share,
            snapshotPolicy: 'legacy-unavailable' as const,
            itemsSnapshot:
              share.itemsSnapshot?.map((item) =>
                migrateLegacyQuoteItem(item as LegacyQuoteItem),
              ) ?? structuredClone(quote.items),
            overallDiscountSnapshot:
              share.overallDiscountSnapshot ?? quote.overallDiscount,
          }
        : share;
    }),
  };
  return hydrateNegotiationState(hydrated, true);
}

function exactLegacyCustomerId(state: CpqState, quote: Quote): string {
  if (quote.customer.customerId?.trim()) {
    if (
      state.catalog.customers.some(
        (customer) => customer.id === quote.customer.customerId,
      )
    )
      return quote.customer.customerId;
    throw new Error('报价绑定的客户目录记录不存在，拒绝迁移分享');
  }
  const matches = state.catalog.customers.filter(
    (customer) => customer.customerNo === quote.customer.customerNo,
  );
  if (matches.length !== 1) {
    throw new Error('旧报价缺少唯一稳定客户绑定，拒绝按名称或电话迁移');
  }
  return matches[0].id;
}

function legacyCustomerSnapshot(
  state: CpqState,
  quote: Quote,
  share: CpqState['shares'][number],
) {
  const customerId = exactLegacyCustomerId(state, quote);
  const provided = share.customerSnapshot;
  if (
    provided &&
    provided.customerId === customerId &&
    provided.customerNo.trim() &&
    provided.name.trim() &&
    typeof provided.contact === 'string' &&
    typeof provided.phone === 'string'
  ) {
    return structuredClone(provided);
  }
  if (provided && provided.customerId !== undefined) {
    throw new Error('旧分享客户快照与报价稳定客户标识错绑');
  }
  return {
    customerId,
    customerNo: quote.customer.customerNo,
    name: quote.customer.name,
    contact: quote.customer.contact,
    phone: quote.customer.phone,
  };
}

/** Controlled v4 import. A malformed old share graph is rejected before any
 * persistence; no current quote/catalog values are copied into publications. */
function hydrateNegotiationState(
  state: CpqState,
  allowLegacyIdentityDefaults = false,
): CpqState {
  const quotes = Object.fromEntries(
    Object.entries(state.quotes).map(([key, quote]) => [
      key,
      { ...quote, masterId: quote.masterId ?? quote.quoteId },
    ]),
  );
  if (state.negotiationLinks || state.publications) {
    if (!state.negotiationLinks || !state.publications)
      throw new Error('协商图不完整');
    const links = state.negotiationLinks.map((link) => {
      if (
        !allowLegacyIdentityDefaults &&
        (!link.subjectId ||
          !link.skinId ||
          !link.subjectLabel ||
          !link.skinLabel)
      )
        throw new Error('现代协商链接缺少稳定主体或皮肤身份');
      return {
        ...link,
        subjectId: link.subjectId ?? 'default-subject',
        skinId: link.skinId ?? 'default-skin',
        subjectLabel: link.subjectLabel ?? '默认公司主体',
        skinLabel: link.skinLabel ?? '默认皮肤',
      };
    });
    const defaultShareStyle = state.shareStyles.find(
      (style) => style.isDefault,
    );
    if (!defaultShareStyle) throw new Error('协商图缺少默认分享样式');
    const publications = state.publications.map((publication) => {
      if (!publication.businessModeLabelSnapshot?.trim())
        throw new Error('协商发布缺少业务模式历史名称快照');
      const withShareStyle = publication.shareStyleSnapshot
        ? publication
        : allowLegacyIdentityDefaults
          ? {
              ...publication,
              shareStyleSnapshot: createShareStyleSnapshot(
                defaultShareStyle,
                publication.createdAt,
              ),
            }
          : (() => {
              throw new Error('现代协商发布缺少分享样式快照');
            })();
      if (withShareStyle.snapshotPolicy !== 'published-channel-v1')
        return withShareStyle;
      if (!hasShareFieldCombinationSnapshot(withShareStyle.combinationSnapshot))
        throw new Error('现代协商发布缺少可验证的字段组合快照');
      if (
        withShareStyle.quotationListProjection &&
        withShareStyle.headerProjection
      )
        return withShareStyle;
      const frozenQuotation = migrateFrozenQuotationProjectionsFromFacts({
        quoteId: withShareStyle.quoteId,
        quoteVersion: withShareStyle.quoteVersion,
        combinationSnapshot: withShareStyle.combinationSnapshot,
        itemsSnapshot: withShareStyle.itemsSnapshot,
        placementRowsSnapshot: withShareStyle.placementRowsSnapshot,
        customerSnapshot: withShareStyle.customerSnapshot,
        validUntilSnapshot: withShareStyle.validUntilSnapshot,
        overallDiscountSnapshot: withShareStyle.overallDiscountSnapshot,
        businessModeLabelSnapshot: withShareStyle.businessModeLabelSnapshot,
      });
      return {
        ...withShareStyle,
        quotationListProjection: frozenQuotation.quotationListProjection,
        headerProjection: frozenQuotation.headerProjection,
      };
    });
    const seenTokens = new Set<string>();
    const seenLinks = new Set<string>();
    const seenLiveLinkIdentities = new Set<string>();
    const seenPublications = new Set<string>();
    for (const link of links) {
      const identity = `${link.quoteMasterId}|${link.subjectId}|${link.skinId}`;
      if (
        !link.id ||
        !link.token ||
        !link.quoteMasterId ||
        !link.customerId ||
        !link.subjectId ||
        !link.skinId ||
        seenLinks.has(link.id) ||
        seenTokens.has(link.token) ||
        (link.lifecycle !== 'revoked' && seenLiveLinkIdentities.has(identity))
      )
        throw new Error('协商链接重复或缺少身份');
      seenLinks.add(link.id);
      seenTokens.add(link.token);
      if (link.lifecycle !== 'revoked') seenLiveLinkIdentities.add(identity);
      if (!['active', 'paused', 'revoked', 'expired'].includes(link.lifecycle))
        throw new Error('协商链接生命周期无效');
      if (
        link.expiresAt &&
        (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(
          link.expiresAt,
        ) ||
          !Number.isFinite(new Date(link.expiresAt).getTime()))
      )
        throw new Error('协商链接有效期无效');
      if (
        link.currentPublicationId &&
        !publications.some(
          (item) =>
            item.id === link.currentPublicationId && item.linkId === link.id,
        )
      )
        throw new Error('协商链接当前发布错绑');
    }
    for (const publication of publications) {
      const link = links.find((item) => item.id === publication.linkId);
      const quote = quotes[publication.quoteId];
      if (
        !publication.id ||
        seenPublications.has(publication.id) ||
        !link ||
        publication.quoteMasterId !== link.quoteMasterId ||
        (quote &&
          (publication.quoteMasterId !== quote.masterId ||
            publication.quoteVersion !== quote.version)) ||
        !Array.isArray(publication.itemsSnapshot) ||
        !['published-channel-v1', 'legacy-unavailable'].includes(
          publication.snapshotPolicy,
        ) ||
        !publication.customerSnapshot ||
        typeof publication.customerSnapshot.customerId !== 'string' ||
        !publication.customerSnapshot.customerId.trim() ||
        typeof publication.customerSnapshot.customerNo !== 'string' ||
        !publication.customerSnapshot.customerNo.trim() ||
        typeof publication.customerSnapshot.name !== 'string' ||
        !publication.customerSnapshot.name.trim() ||
        typeof publication.customerSnapshot.contact !== 'string' ||
        typeof publication.customerSnapshot.phone !== 'string' ||
        link.customerId !== publication.customerSnapshot.customerId ||
        typeof publication.validUntilSnapshot !== 'string' ||
        typeof publication.businessModeLabelSnapshot !== 'string' ||
        !publication.businessModeLabelSnapshot.trim() ||
        !publication.itemsSnapshot.every(hasStablePublicShareItem) ||
        (publication.businessSectionsSnapshot !== undefined &&
          !hasQuoteOutputBusinessSectionsSnapshot(
            publication.businessSectionsSnapshot,
          )) ||
        !Array.isArray(publication.selectionOptionsSnapshot) ||
        !publication.selectionOptionsSnapshot.every(hasStablePublicShareItem) ||
        !Array.isArray(publication.publicFieldSnapshot) ||
        !Number.isFinite(publication.overallDiscountSnapshot) ||
        !isKnownShareStyleSnapshot(state, publication.shareStyleSnapshot) ||
        (publication.snapshotPolicy === 'published-channel-v1' &&
          (!hasShareFieldCombinationSnapshot(publication.combinationSnapshot) ||
            !publication.quotationListProjection ||
            !publication.headerProjection ||
            frozenQuotationListProjectionIssue(
              publication.quotationListProjection,
            ) !== undefined ||
            frozenQuotationHeaderProjectionIssue(
              publication.headerProjection,
              publication.combinationSnapshot,
            ) !== undefined ||
            publication.quotationListProjection.quoteId !==
              publication.quoteId ||
            publication.quotationListProjection.quoteVersion !==
              publication.quoteVersion ||
            publication.headerProjection.quoteId !== publication.quoteId ||
            publication.headerProjection.quoteVersion !==
              publication.quoteVersion ||
            JSON.stringify(
              publication.quotationListProjection.combinationSnapshot,
            ) !== JSON.stringify(publication.combinationSnapshot) ||
            publication.headerProjection.customerSnapshot.customerId !==
              publication.customerSnapshot.customerId ||
            publication.headerProjection.validUntilSnapshot !==
              publication.validUntilSnapshot ||
            publication.headerProjection.overallDiscountSnapshot !==
              publication.overallDiscountSnapshot)) ||
        (publication.combinationSnapshot !== undefined &&
          !hasShareFieldCombinationSnapshot(publication.combinationSnapshot))
      ) {
        throw new Error('协商发布图错绑或冻结快照无效');
      }
      seenPublications.add(publication.id);
    }
    for (const link of links) {
      if (link.currentPublicationId) {
        const current = publications.find(
          (publication) => publication.id === link.currentPublicationId,
        );
        if (
          !current ||
          current.customerSnapshot.customerId !== link.customerId
        ) {
          throw new Error('协商链接当前客户快照错绑');
        }
      }
    }
    return { ...state, quotes, publications };
  }
  if (!allowLegacyIdentityDefaults) {
    throw new Error('现代状态缺少固定链接与发布稳定对象图');
  }
  const tokenSet = new Set<string>();
  const links: NonNullable<CpqState['negotiationLinks']> = state.shares.map(
    (share) => {
      if (!share.token || tokenSet.has(share.token))
        throw new Error('分享 token 重复');
      tokenSet.add(share.token);
      const quote = quotes[share.quoteId];
      if (
        !quote ||
        quote.version !== share.quoteVersion ||
        !share.itemsSnapshot ||
        share.overallDiscountSnapshot === undefined ||
        (share.combinationSnapshot !== undefined &&
          !hasShareFieldCombinationSnapshot(share.combinationSnapshot))
      )
        throw new Error('旧分享图错绑或缺少冻结快照');
      return {
        id: `LINK-${share.id}`,
        token: share.token,
        quoteMasterId: quote.masterId ?? quote.quoteId,
        subjectId: 'default-subject',
        skinId: 'default-skin',
        subjectLabel: '默认公司主体',
        skinLabel: '默认皮肤',
        customerId: exactLegacyCustomerId(state, quote),
        customerName: quote.customer.name,
        customerPhone: quote.customer.phone,
        allowSelection: share.allowSelection,
        lifecycle:
          share.status === 'revoked'
            ? ('revoked' as const)
            : ('active' as const),
        createdAt: share.createdAt,
        updatedAt: share.createdAt,
      };
    },
  );
  if (
    new Set(
      links.map(
        (link) => `${link.quoteMasterId}|${link.subjectId}|${link.skinId}`,
      ),
    ).size !== links.length
  )
    throw new Error('旧分享链接身份重复，无法安全迁移');
  const defaultShareStyle = state.shareStyles.find((style) => style.isDefault);
  if (!defaultShareStyle) throw new Error('旧分享迁移缺少默认分享样式');
  const publications = state.shares.map((share) => {
    const link = links.find((item) => item.token === share.token);
    const quote = quotes[share.quoteId];
    if (
      !link ||
      !quote ||
      !share.itemsSnapshot ||
      share.overallDiscountSnapshot === undefined
    )
      throw new Error('旧分享图错绑');
    const id = `PUB-${share.id}`;
    link.currentPublicationId = id;
    let shareStyleSnapshot = share.shareStyleSnapshot;
    if (shareStyleSnapshot) {
      if (!isKnownShareStyleSnapshot(state, shareStyleSnapshot))
        throw new Error('旧分享样式快照无效或绑定不存在');
      shareStyleSnapshot = structuredClone(shareStyleSnapshot);
    } else {
      shareStyleSnapshot = createShareStyleSnapshot(
        defaultShareStyle,
        share.createdAt,
      );
    }
    const publicationCustomerSnapshot = share.customerSnapshot
      ? structuredClone(share.customerSnapshot)
      : legacyCustomerSnapshot(state, quote, share);
    const publicationValidUntil = share.validUntilSnapshot ?? quote.validUntil;
    // A legacy graph may still be link-compatible while lacking enough
    // frozen facts to prove a typed quotation-list binding. Keep that link as
    // unavailable rather than reading a current quote/catalog to fill fields.
    const canMigrateFrozenList =
      share.snapshotPolicy === 'published-channel-v1' &&
      hasShareFieldCombinationSnapshot(share.combinationSnapshot) &&
      share.customerSnapshot !== undefined &&
      share.validUntilSnapshot !== undefined;
    let frozenQuotation:
      | ReturnType<typeof migrateFrozenQuotationProjectionsFromFacts>
      | undefined;
    if (
      canMigrateFrozenList &&
      share.combinationSnapshot &&
      share.customerSnapshot &&
      share.validUntilSnapshot
    ) {
      frozenQuotation = migrateFrozenQuotationProjectionsFromFacts({
        quoteId: share.quoteId,
        quoteVersion: share.quoteVersion,
        combinationSnapshot: share.combinationSnapshot,
        itemsSnapshot: share.itemsSnapshot,
        placementRowsSnapshot: undefined,
        customerSnapshot: share.customerSnapshot,
        validUntilSnapshot: share.validUntilSnapshot,
        overallDiscountSnapshot: share.overallDiscountSnapshot,
      });
    }
    return {
      id,
      linkId: link.id,
      quoteMasterId: quote.masterId ?? quote.quoteId,
      quoteId: quote.quoteId,
      quoteVersion: quote.version,
      snapshotPolicy: frozenQuotation
        ? ('published-channel-v1' as const)
        : ('legacy-unavailable' as const),
      customerSnapshot: publicationCustomerSnapshot,
      validUntilSnapshot: publicationValidUntil,
      businessModeLabelSnapshot: quote.businessModeLabelSnapshot,
      itemsSnapshot: share.itemsSnapshot.map((item) => structuredClone(item)),
      overallDiscountSnapshot: share.overallDiscountSnapshot,
      publicFieldSnapshot: [],
      selectionOptionsSnapshot: [],
      allowSelection: share.allowSelection,
      ...(share.combinationSnapshot
        ? { combinationSnapshot: structuredClone(share.combinationSnapshot) }
        : {}),
      ...(frozenQuotation
        ? {
            quotationListProjection: frozenQuotation.quotationListProjection,
            headerProjection: frozenQuotation.headerProjection,
          }
        : {}),
      shareStyleSnapshot,
      createdAt: share.createdAt,
    };
  });
  return { ...state, quotes, negotiationLinks: links, publications };
}

type ParsedStoredState =
  | { status: 'valid-v5'; state: CpqState }
  | { status: 'migrated-v4'; state: CpqState }
  | { status: 'migrated-v3'; state: CpqState }
  | {
      status:
        | 'malformed-v5'
        | 'unsupported-v5'
        | 'malformed-v4'
        | 'unsupported-v4'
        | 'malformed-v3'
        | 'unsupported-v3';
      error?: unknown;
    };

function parseStoredState(
  raw: string,
  expectedVersion: 3 | 4 | 5,
): ParsedStoredState {
  const malformedStatus = `malformed-v${expectedVersion}` as const;
  const unsupportedStatus = `unsupported-v${expectedVersion}` as const;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return { status: malformedStatus };
    }
    const schemaVersion = (parsed as { schemaVersion?: unknown }).schemaVersion;
    if (schemaVersion !== expectedVersion) {
      return {
        status:
          typeof schemaVersion === 'number'
            ? unsupportedStatus
            : malformedStatus,
      };
    }
    if (!hasCoreStateShape(parsed)) return { status: malformedStatus };
    if (expectedVersion === 5) {
      // An active revision price must remain unknown or a real nonnegative
      // amount; hydration must not turn a malformed persisted price into null.
      if (
        parsed.catalog.devices.some(
          (device) =>
            device.packageItemPrice !== undefined &&
            device.packageItemPrice !== null &&
            (!Number.isFinite(device.packageItemPrice) ||
              device.packageItemPrice < 0),
        )
      )
        return { status: malformedStatus };
      if (
        parsed.catalog.devices.some(
          (device) =>
            !hasStablePurchasePriceFact(
              device.buyoutFinalPriceTax13,
              device.pricingFieldAvailability?.buyoutFinalPriceTax13,
            ) ||
            !hasStablePurchasePriceFact(
              device.purchaseFinalPriceExTax13,
              device.pricingFieldAvailability?.purchaseFinalPriceExTax13,
            ),
        )
      )
        return { status: malformedStatus };
      const defaultIdentity = createDemoState();
      const businessState = parsed as CpqPersistedState;
      const hydrated = hydrateNegotiationState(
        hydrateQuoteFieldConfiguration(
          {
            ...businessState,
            currentRoleId: defaultIdentity.currentRoleId,
            role: defaultIdentity.role,
            currentActorUserId: defaultIdentity.currentActorUserId,
          },
          5,
        ),
        false,
      );
      const completed = ensureDemoManagementQuoteRows(
        hydrated,
        defaultIdentity,
      );
      return hasStableSources(completed)
        ? { status: 'valid-v5', state: completed }
        : { status: 'malformed-v5' };
    }
    if (expectedVersion === 4) {
      const migrated = hydrateNegotiationState(
        hydrateQuoteFieldConfiguration(
          {
            ...(parsed as unknown as LegacyV4State),
            schemaVersion: 5,
            currentRoleId:
              (parsed as unknown as LegacyV4State).currentRoleId ?? '',
          },
          4,
        ),
        true,
      );
      return hasStableSources(migrated)
        ? { status: 'migrated-v4', state: migrated }
        : { status: 'malformed-v4' };
    }
    const migrated = hydrateQuoteFieldConfiguration(
      migrateV3State(parsed as LegacyCpqState),
      3,
    );
    const defaultIdentity = createDemoState();
    const migratedWithIdentity = migrated.currentActorUserId
      ? migrated
      : {
          ...migrated,
          currentRoleId: defaultIdentity.currentRoleId,
          role: defaultIdentity.role,
          currentActorUserId: defaultIdentity.currentActorUserId,
        };
    return hasStableSources(migratedWithIdentity)
      ? { status: 'migrated-v3', state: migratedWithIdentity }
      : { status: 'malformed-v3' };
  } catch (error) {
    return { status: malformedStatus, error };
  }
}

/**
 * Keep 50 management-eligible Disposable Mock rows for browsers that already
 * persisted an earlier fixture set. Existing quote edits win; only missing
 * demo outcome facts and missing demo rows are added.
 */
function ensureDemoManagementQuoteRows(
  state: CpqState,
  defaultState: CpqState,
): CpqState {
  if (state.localDemoScenarioVersion === 1) return state;
  const hasExplicitlyClearedCustomerHistory =
    Object.keys(state.quotes).length === 1 &&
    Boolean(state.quotes['Q-V2-NEW-0001']) &&
    state.shares.length === 0 &&
    (state.negotiationLinks ?? []).length === 0 &&
    (state.publications ?? []).length === 0 &&
    state.negotiationDrafts.length === 0 &&
    state.receipts.length === 0 &&
    state.audits.length === 0;
  if (hasExplicitlyClearedCustomerHistory) return state;

  const targetCount = 50;
  const legacyMultiRoundReceiptIds = [
    'RECEIPT-0003',
    'RECEIPT-0004',
    'RECEIPT-0005',
  ];
  const shouldUpgradeLegacyMultiRoundFeedback =
    legacyMultiRoundReceiptIds.every((receiptId) =>
      state.receipts.some(
        (receipt) =>
          receipt.id === receiptId &&
          receipt.shareId === 'SHARE-0001' &&
          receipt.sourceQuoteId === 'Q-V2-0002' &&
          receipt.sourceVersion === 1 &&
          !receipt.publicationId &&
          !receipt.linkId &&
          !receipt.quoteMasterId,
      ),
    );
  const currentPublicationIds = new Set(
    (state.negotiationLinks ?? []).flatMap((link) =>
      link.currentPublicationId ? [link.currentPublicationId] : [],
    ),
  );
  const publishedMasterIds = new Set(
    (state.publications ?? [])
      .filter((publication) => currentPublicationIds.has(publication.id))
      .map((publication) => publication.quoteMasterId),
  );
  const managementQuoteNumberCount = (quotes: Record<string, Quote>) =>
    new Set(
      Object.values(quotes)
        .filter(
          (quote) =>
            Boolean(quote.salesOrderId?.trim()) ||
            publishedMasterIds.has(quote.masterId ?? quote.quoteId),
        )
        .map((quote) => quote.masterId ?? quote.quoteId),
    ).size;
  const quotes = Object.fromEntries(
    Object.entries(state.quotes).map(([quoteId, quote]) => {
      const seededSalesOrderId = defaultState.quotes[quoteId]?.salesOrderId;
      const hasLegacyDemoSalesOrderId =
        typeof quote.salesOrderId === 'string' &&
        /^(?:SO-MOCK-\d{4}|SO-DEMO-20260811-\d{3})$/.test(quote.salesOrderId);
      return [
        quoteId,
        (!quote.salesOrderId || hasLegacyDemoSalesOrderId) && seededSalesOrderId
          ? { ...quote, salesOrderId: seededSalesOrderId }
          : quote,
      ];
    }),
  );
  if (shouldUpgradeLegacyMultiRoundFeedback) {
    for (const quoteId of ['Q-V2-MOCK-0002-V2', 'Q-V2-MOCK-0002-V3']) {
      const candidate = defaultState.quotes[quoteId];
      if (candidate && !quotes[quoteId]) {
        quotes[quoteId] = structuredClone(candidate);
      }
    }
  }
  if (managementQuoteNumberCount(quotes) < targetCount) {
    for (const candidate of Object.values(defaultState.quotes)) {
      if (managementQuoteNumberCount(quotes) >= targetCount) break;
      if (candidate.quoteId === 'Q-V2-NEW-0001' || quotes[candidate.quoteId]) {
        continue;
      }
      quotes[candidate.quoteId] = structuredClone(candidate);
    }
  }
  const shares = [...state.shares];
  const shareIds = new Set(shares.map((share) => share.id));
  for (const candidate of defaultState.shares) {
    if (shareIds.has(candidate.id)) continue;
    const sourceExists = Object.values(quotes).some(
      (quote) =>
        quote.quoteId === candidate.quoteId &&
        quote.version === candidate.quoteVersion,
    );
    if (!sourceExists) continue;
    shares.push(structuredClone(candidate));
    shareIds.add(candidate.id);
  }
  const negotiationLinks = [...(state.negotiationLinks ?? [])];
  const publications = [...(state.publications ?? [])];
  const linkIds = new Set(negotiationLinks.map((link) => link.id));
  const linkedMasterIds = new Set(
    negotiationLinks.map((link) => link.quoteMasterId),
  );
  const publicationIds = new Set(
    publications.map((publication) => publication.id),
  );
  if (shouldUpgradeLegacyMultiRoundFeedback) {
    const defaultLink = defaultState.negotiationLinks?.find(
      (link) => link.quoteMasterId === 'Q-V2-0002',
    );
    const currentLinkIndex = negotiationLinks.findIndex(
      (link) => link.quoteMasterId === 'Q-V2-0002',
    );
    if (defaultLink && currentLinkIndex >= 0) {
      negotiationLinks[currentLinkIndex] = {
        ...negotiationLinks[currentLinkIndex],
        currentPublicationId: defaultLink.currentPublicationId,
        updatedAt: defaultLink.updatedAt,
      };
      for (const publicationId of ['PUB-1101', 'PUB-1102']) {
        if (publicationIds.has(publicationId)) continue;
        const candidate = defaultState.publications?.find(
          (publication) => publication.id === publicationId,
        );
        if (!candidate) continue;
        publications.push(structuredClone(candidate));
        publicationIds.add(publicationId);
      }
    }
  }
  for (const candidate of defaultState.negotiationLinks ?? []) {
    if (
      linkIds.has(candidate.id) ||
      linkedMasterIds.has(candidate.quoteMasterId)
    ) {
      continue;
    }
    const candidatePublication = defaultState.publications?.find(
      (publication) => publication.id === candidate.currentPublicationId,
    );
    if (!candidatePublication || publicationIds.has(candidatePublication.id)) {
      continue;
    }
    negotiationLinks.push(structuredClone(candidate));
    publications.push(structuredClone(candidatePublication));
    linkIds.add(candidate.id);
    linkedMasterIds.add(candidate.quoteMasterId);
    publicationIds.add(candidatePublication.id);
  }
  const receipts = state.receipts.map((receipt) => {
    const candidate = defaultState.receipts.find(
      (item) => item.id === receipt.id,
    );
    if (
      shouldUpgradeLegacyMultiRoundFeedback &&
      legacyMultiRoundReceiptIds.includes(receipt.id)
    ) {
      return candidate ? structuredClone(candidate) : receipt;
    }
    const hasEarlierCameraIncrement =
      receipt.id === 'RECEIPT-0005' &&
      receipt.quoteMasterId === 'Q-V2-0002' &&
      receipt.publicationId === 'PUB-1102' &&
      receipt.note === '客户再次反馈：增加一支麦克风，并要求重新确认配置' &&
      receipt.selectionSnapshot.kind === 'structured' &&
      receipt.selectionSnapshot.items.some(
        (item) =>
          item.selectionKind === 'added' &&
          item.effectiveItem?.name === 'Sony A7M4',
      );
    if (hasEarlierCameraIncrement && candidate) {
      return {
        ...receipt,
        selectionSnapshot: structuredClone(candidate.selectionSnapshot),
      };
    }
    return receipt;
  });
  const receiptIds = new Set(receipts.map((receipt) => receipt.id));
  for (const candidate of defaultState.receipts) {
    if (receiptIds.has(candidate.id)) continue;
    receipts.push(structuredClone(candidate));
  }
  const quotesChanged = Object.entries(quotes).some(
    ([quoteId, quote]) => quote !== state.quotes[quoteId],
  );
  const receiptsChanged = receipts.some(
    (receipt, index) => receipt !== state.receipts[index],
  );
  const sharesChanged = shares.length !== state.shares.length;
  const linksChanged =
    negotiationLinks.length !== (state.negotiationLinks ?? []).length ||
    negotiationLinks.some(
      (link, index) => link !== state.negotiationLinks?.[index],
    );
  const publicationsChanged =
    publications.length !== (state.publications ?? []).length;
  return quotesChanged ||
    receiptsChanged ||
    sharesChanged ||
    linksChanged ||
    publicationsChanged
    ? {
        ...state,
        quotes,
        shares,
        receipts,
        negotiationLinks,
        publications,
      }
    : state;
}

export function loadCpqState(storage: StorageLike): CpqLoadResult {
  const fallback = hydrateQuoteFieldConfiguration(createDemoState());
  let rawV5: string | null;
  try {
    rawV5 = storage.getItem(CPQ_STORAGE_KEY);
  } catch (error) {
    return {
      state: fallback,
      status: 'storage-read-error',
      autoPersist: false,
      error,
    };
  }

  if (rawV5 !== null) {
    const parsed = parseStoredState(rawV5, 5);
    return 'state' in parsed
      ? { ...parsed, autoPersist: true }
      : {
          state: fallback,
          status: parsed.status,
          autoPersist: false,
          error: parsed.error,
        };
  }

  let rawV4: string | null;
  try {
    rawV4 = storage.getItem(CPQ_V4_STORAGE_KEY);
  } catch (error) {
    return {
      state: fallback,
      status: 'storage-read-error',
      autoPersist: false,
      error,
    };
  }
  if (rawV4 !== null) {
    const parsed = parseStoredState(rawV4, 4);
    return 'state' in parsed
      ? { ...parsed, autoPersist: true }
      : {
          state: fallback,
          status: parsed.status,
          autoPersist: false,
          error: parsed.error,
        };
  }

  let rawV3: string | null;
  try {
    rawV3 = storage.getItem(CPQ_LEGACY_STORAGE_KEY);
  } catch (error) {
    return {
      state: fallback,
      status: 'storage-read-error',
      autoPersist: false,
      error,
    };
  }

  if (rawV3 === null) {
    return { state: fallback, status: 'missing', autoPersist: true };
  }
  const parsed = parseStoredState(rawV3, 3);
  return 'state' in parsed
    ? { ...parsed, autoPersist: true }
    : {
        state: fallback,
        status: parsed.status,
        autoPersist: false,
        error: parsed.error,
      };
}

export function persistCpqState(storage: StorageLike, state: CpqState): void {
  const {
    currentRoleId: _currentRoleId,
    currentActorUserId: _currentActorUserId,
    role: _role,
    ...runtimeBusinessState
  } = state;
  const sessionSafeBusinessState = {
    ...runtimeBusinessState,
    quotes: Object.fromEntries(
      Object.entries(runtimeBusinessState.quotes).map(([quoteId, quote]) => {
        const { orderConfirmation: _orderConfirmation, ...safeQuote } = quote;
        return [quoteId, safeQuote];
      }),
    ),
  };
  const businessState = projectDeviceMediaForStorage(sessionSafeBusinessState);
  // 容量门禁必须针对真正写入 LocalStorage 的业务载荷；运行时验收身份
  // 与角色派生对象不会写入持久化值，不能把它们重复计入导入新版本预算。
  assertMockStorageCapacity(businessState);
  validatePersistablePurchasePrices(businessState.catalog.devices);
  const serialized = JSON.stringify(businessState satisfies CpqPersistedState);
  const validated = parseStoredState(serialized, 5);
  if (validated.status !== 'valid-v5') {
    const detail =
      'error' in validated && validated.error instanceof Error
        ? `：${validated.error.message}`
        : '';
    throw new Error(`拒绝持久化不满足 v5 领域不变量的状态${detail}`);
  }
  const {
    currentRoleId: _validatedRoleId,
    currentActorUserId: _validatedActorId,
    role: _validatedRole,
    ...validatedBusinessState
  } = validated.state;
  storage.setItem(CPQ_STORAGE_KEY, JSON.stringify(validatedBusinessState));
}

export function persistCpqLoadResult(
  storage: StorageLike,
  loaded: CpqLoadResult,
  state: CpqState,
): CpqPersistResult {
  if (!loaded.autoPersist) return { status: 'skipped' };
  try {
    persistCpqState(storage, state);
  } catch (error) {
    return { status: 'storage-write-error', error };
  }
  if (loaded.status !== 'migrated-v3' && loaded.status !== 'migrated-v4') {
    return { status: 'persisted-v5' };
  }
  try {
    storage.removeItem(
      loaded.status === 'migrated-v4'
        ? CPQ_V4_STORAGE_KEY
        : CPQ_LEGACY_STORAGE_KEY,
    );
    return { status: 'migration-complete' };
  } catch (error) {
    return { status: 'legacy-cleanup-error', error };
  }
}

export function resetCpqState(storage: StorageLike): CpqState {
  storage.removeItem(CPQ_LEGACY_STORAGE_KEY);
  storage.removeItem(CPQ_V4_STORAGE_KEY);
  storage.removeItem(CPQ_STORAGE_KEY);
  return createDemoState();
}

/**
 * Clears only quote/customer binding history in the Disposable LocalStorage
 * Mock. The catalog remains the caller's current catalog, so product,
 * inventory, media, field and permission fixtures are not silently rebuilt.
 * A single validated v5 write gives callers all-or-nothing state semantics.
 */
export function clearCustomerBindingHistory(storage: StorageLike): CpqState {
  const loaded = loadCpqState(storage);
  if (
    !['missing', 'valid-v5', 'migrated-v4', 'migrated-v3'].includes(
      loaded.status,
    )
  )
    throw new Error('Disposable Mock 当前状态不可安全清理');

  const quoteTemplate =
    loaded.state.quotes['Q-V2-NEW-0001'] ??
    loaded.state.quotes[loaded.state.activeQuoteId] ??
    Object.values(loaded.state.quotes)[0];
  if (!quoteTemplate) throw new Error('Disposable Mock 缺少报价默认合同');
  const {
    quoteId: _quoteId,
    masterId: _masterId,
    sourceQuoteId: _sourceQuoteId,
    sourceVersion: _sourceVersion,
    sourceReceiptId: _sourceReceiptId,
    versionWorkflow: _versionWorkflow,
    title: _title,
    customer: _customer,
    deposit: _deposit,
    customFieldValues: _customFieldValues,
    sourceFieldValueSnapshot: _sourceFieldValueSnapshot,
    items: _items,
    itemAdjustments: _itemAdjustments,
    calculationBindings: _calculationBindings,
    status: _status,
    dirty: _dirty,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    activeOperationId: _activeOperationId,
    failureReason: _failureReason,
    salesOrderId: _salesOrderId,
    source: _source,
    submittedBy: _submittedBy,
    orderConfirmation: _orderConfirmation,
    ...quoteDefaults
  } = structuredClone(quoteTemplate);
  const blankQuote: Quote = {
    ...quoteDefaults,
    quoteId: 'Q-V2-NEW-0001',
    masterId: 'Q-V2-NEW-0001',
    version: 1,
    title: '新报价单',
    customer: { name: '', customerNo: '', contact: '', phone: '' },
    deposit: 0,
    overallDiscount: 100,
    items: [],
    itemAdjustments: [],
    status: 'draft',
    dirty: false,
    createdAt: quoteTemplate.createdAt,
    updatedAt: quoteTemplate.updatedAt,
  };
  const next: CpqState = {
    ...loaded.state,
    activeView: 'workspace',
    activeQuoteId: blankQuote.quoteId,
    openQuoteIds: [blankQuote.quoteId],
    quotes: { [blankQuote.quoteId]: blankQuote },
    shares: [],
    negotiationLinks: [],
    publications: [],
    negotiationDrafts: [],
    receipts: [],
    stockReminders: [],
    activities: [],
    audits: [],
  };
  persistCpqState(storage, next);
  return next;
}

export function createMemoryStorage(
  initial: Record<string, string> = {},
): StorageLike {
  const data = new Map(Object.entries(initial));
  return {
    getItem(key) {
      return data.get(key) ?? null;
    },
    setItem(key, value) {
      data.set(key, value);
    },
    removeItem(key) {
      data.delete(key);
    },
  };
}
